/**
 * View: Nova proposta (form de criação - contrato recorrente).
 *
 * Layout em 2 colunas:
 *   - Esquerda: form com 7 seções (Cliente, Diagnóstico, Segurança&Backup,
 *     Recursos, Dispositivos, Add-ons, Perfil&Onboarding)
 *   - Direita: painel sticky com breakdown de pricing reativo
 *
 * Pricing roda no input/change de cada campo relevante (recursos, perfil,
 * pacote, onboarding, add-ons). Lib pura em `lib/pricing.ts`.
 *
 * Diagnóstico + Segurança/Backup + Inventário + Observações livres são
 * serializados em Markdown no campo `observacoes` (compat com schema atual).
 *
 * Estilos vivem em `proposta-nova.css` (import abaixo) — antes ficavam inline
 * num template literal, mas isso sofreu com truncamento de sync de mount.
 */

import './proposta-nova.css';

import {
  criarPropostaContratoRecorrenteSchema,
  sugerirHorasSuporteEssencial,
  horasPremiumModuloPorEstacoes,
  parsePrazoFidelidadeMeses,
  type CriarPropostaInput,
  type HaasItemTipo,
  type PrecosHaasItem,
  type Proposta,
  type PropostaHaasItem,
  type PropostaPremiumModulo,
  type TabelaPrecosCompleta,
} from '@infomatic/shared';

import { api, ApiError } from '../../lib/api.js';
import { getUsuarioLogado } from '../../lib/auth.js';
import {
  calcularPricing,
  calcularOnboardingSugerido,
  fmtBRL,
  ADDONS_VAZIO,
  type AddOns,
  type Breakdown,
  type Inventario,
  type LinhaBreakdown,
  type OnboardingInput,
  type OnboardingSugerido,
  type PacoteKey,
  type PerfilKey,
} from '../../lib/pricing.js';
import { escapeHtml, renderLayout } from '../layout.js';
import { getRouter } from '../router.js';

interface FormState {
  loadingOmie: boolean;
  submitting: boolean;
  errors: Record<string, string>;
  topLevelError: string | null;
  tabela: TabelaPrecosCompleta | null;
  modoEdicao: boolean;
  propostaId: string | null;
  /** Tipo da proposta — define se form mostra blocos de renovação (#219). */
  tipoProposta: 'contrato-recorrente' | 'renovacao-recorrente';
  /** Itens HaaS contratados — array editável, persistido no payload.haasItens */
  haasItens: PropostaHaasItem[];
  /** Módulos Premium selecionados (snapshot por proposta — Contrato v2026.08). */
  premiumModulosSelecionados: PropostaPremiumModulo[];
  /** Congelar reajuste IPCA — marcador comercial (#255) */
  congelarReajusteAtivo: boolean;
  congelarReajusteMeses: number;
  /** Desconto promocional escalonado — opcional (vendedor decide aplicar) */
  descontoAtivo: boolean;
  descontoFaixas: Array<{
    tipo: 'pct' | 'valor';
    valor: number;
    duracaoMeses: number;
    motivo?: string;
  }>;
  descontoAplicarEm: 'mensal' | 'mensal-onboarding';
  // ── Renovação (#219) — usado quando tipoProposta='renovacao-recorrente' ──
  /** Snapshot do contrato Omie selecionado pra renovar. Vem do modal Omie. */
  contratoOrigem: {
    codigoOmie: number;
    numeroOmie: string;
    dataInicio: string;
    dataTermino: string;
    valorMensalAtual: number;
  } | null;
  reajusteTipo: 'ipca' | 'igpm' | 'manual' | 'sem-reajuste';
  reajustePercentual: number;
  reajusteMotivo: string;
  novaVigenciaDataInicio: string;
  novaVigenciaPrazoMeses: 12 | 24 | 36;
}

const state: FormState = {
  loadingOmie: false,
  submitting: false,
  errors: {},
  topLevelError: null,
  tabela: null,
  modoEdicao: false,
  propostaId: null,
  tipoProposta: 'contrato-recorrente',
  haasItens: [],
  premiumModulosSelecionados: [],
  congelarReajusteAtivo: false,
  congelarReajusteMeses: 12,
  descontoAtivo: false,
  descontoFaixas: [],
  descontoAplicarEm: 'mensal',
  contratoOrigem: null,
  reajusteTipo: 'ipca',
  reajustePercentual: 0,
  reajusteMotivo: '',
  novaVigenciaDataInicio: '',
  novaVigenciaPrazoMeses: 12,
};

export interface RenderNovaPropostaOpts {
  tipoProposta?: 'contrato-recorrente' | 'renovacao-recorrente';
  /** CNPJ pré-preenchido (PR-R3, vem do dashboard de renovações). */
  cnpjPreFill?: string;
  /** Código do contrato Omie pré-selecionado (PR-R3). */
  contratoOmiePreFill?: string;
}

export async function renderNovaProposta(
  propostaId?: string,
  opts?: RenderNovaPropostaOpts,
): Promise<void> {
  state.modoEdicao = !!propostaId;
  state.propostaId = propostaId ?? null;
  state.tipoProposta = opts?.tipoProposta ?? 'contrato-recorrente';
  const $app = document.getElementById('app');
  if (!$app) return;

  const usuario = getUsuarioLogado();
  if (!usuario) {
    $app.innerHTML = renderLayout({
      title: 'Nova proposta',
      activeNav: 'propostas',
      bodyHtml: `<section class="card"><p>Faça login pra criar propostas.</p></section>`,
    });
    return;
  }

  const titulo = state.modoEdicao ? 'Editar proposta' : 'Nova proposta';
  $app.innerHTML = renderLayout({
    title: titulo,
    activeNav: 'propostas',
    bodyHtml: renderShell(titulo),
  });

  injectFormStyles();
  attachHandlers(usuario.email, usuario.nome);

  // Carrega tabela de preços + (se edição) proposta atual em paralelo
  try {
    const [precosResp, propostaResp] = await Promise.all([
      api.admin.precosGet(),
      state.propostaId ? api.propostas.get(state.propostaId) : Promise.resolve(null),
    ]);
    state.tabela = precosResp.tabela;
    if (propostaResp) {
      popularFormComProposta(propostaResp.proposta);
    }

    // PR-R3: pré-popular renovação a partir de query params do dashboard.
    if (
      state.tipoProposta === 'renovacao-recorrente' &&
      opts?.cnpjPreFill &&
      opts?.contratoOmiePreFill &&
      !state.modoEdicao
    ) {
      await prePopularRenovacaoOmie(opts.cnpjPreFill, opts.contratoOmiePreFill);
    }

    recalcularPricing();
  } catch (err) {
    const $pricing = document.getElementById('pricing-panel');
    if ($pricing) {
      $pricing.innerHTML = `
        <div style="color:var(--color-error,#c00);font-size:var(--fs-sm)">
          Falha ao carregar dados. ${err instanceof Error ? escapeHtml(err.message) : ''}
        </div>`;
    }
  }
}

/**
 * Popula os campos do form a partir de uma proposta existente.
 * Usada no modo edição (rota /propostas/:id/editar).
 * Só rola se a proposta for contrato-recorrente — outros tipos têm form próprio.
 */
/**
 * PR-R3: Pré-popular form de renovação a partir de CNPJ + código do contrato Omie.
 * Fluxo:
 *  1) lookup do cliente Omie pelo CNPJ → preenche dados do cliente no form
 *  2) busca detalhe do contrato Omie pelo código → preenche state.contratoOrigem +
 *     adiciona itens do contrato como linhas de inventário em observações markdown
 *  3) calcula nova vigência (dataTermino do contrato origem + 1d) + prazo 12m default
 *  4) reajuste default = IPCA (vendedor edita)
 */
async function prePopularRenovacaoOmie(cnpj: string, contratoOmie: string): Promise<void> {
  const $err = document.getElementById('top-error');
  try {
    // 1) Cliente Omie → preenche campos de cliente
    // Names dos inputs no form: documento, empresa, responsavel, email, telefone
    // (não usa prefixo "cliente." — ver renderSecaoCliente)
    // eslint-disable-next-line no-console
    console.log('[renovacao] lookup cliente Omie:', cnpj);
    const clienteResp = await api.clientes.lookupOmie(cnpj);
    const c = clienteResp.cliente;
    // eslint-disable-next-line no-console
    console.log('[renovacao] cliente retornado:', c);
    setVal('input[name="documento"]', c.cnpj ?? c.cpf ?? cnpj);
    setVal('input[name="empresa"]', c.empresa);
    setVal('input[name="responsavel"]', c.responsavel);
    setVal('input[name="email"]', c.email);
    if (c.telefone) setVal('input[name="telefone"]', c.telefone);

    // 2) Detalhe do contrato Omie
    const url = `${import.meta.env.VITE_API_BASE_URL ?? ''}/api/clientes/omie/contratos/${encodeURIComponent(contratoOmie)}`;
    const tokenResp = await api.contratos.vencendo(1, false).catch(() => null); // só pra reusar token MSAL
    void tokenResp; // not used directly
    const respCtr = await fetch(url, {
      headers: { Authorization: `Bearer ${getAuthTokenFromSession()}` },
    });
    if (!respCtr.ok) {
      throw new Error(`Detalhe contrato Omie falhou (${respCtr.status})`);
    }
    const detalhe = (await respCtr.json()) as {
      numero: string;
      situacao: string;
      dataInicio: string;
      dataTermino: string;
      valorTotal: number;
      itens: Array<{
        descricao: string;
        codigoServico: string;
        quantidade: number;
        valorUnitario: number;
        valorTotal: number;
      }>;
    };

    state.contratoOrigem = {
      codigoOmie: Number(contratoOmie),
      numeroOmie: detalhe.numero,
      dataInicio: detalhe.dataInicio,
      dataTermino: detalhe.dataTermino,
      valorMensalAtual: detalhe.valorTotal,
    };

    // 3) Nova vigência: dataTermino + 1 dia
    state.novaVigenciaDataInicio = somarDiaIso(parseBrDate(detalhe.dataTermino), 1);
    state.novaVigenciaPrazoMeses = 12;

    // 4) Itens importados → markdown em observações pra parser ler como inventário/add-ons
    const obs = montarMarkdownItensRenovacao(detalhe);
    setVal('textarea[name="observacoes"]', obs);
  } catch (err) {
    if ($err) {
      $err.style.display = 'block';
      $err.textContent = `Pré-popular renovação falhou: ${err instanceof Error ? err.message : String(err)}`;
    }
  }
}

function setVal(selector: string, value: string): void {
  // Aceita seletor CSS completo OU nome simples (que é tratado como name=/id=).
  // Bug #252: chamadas como setVal('pacotePrincipal', x) eram tratadas como
  // tag <pacotePrincipal> pelo querySelector e falhavam silenciosamente —
  // o form de edição carregava sempre os defaults do HTML.
  const isCssSelector = /[#.[\]:>~+\s,*]/.test(selector);
  const el = isCssSelector
    ? document.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(selector)
    : (document.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
        `[name="${selector}"]`,
      ) ??
      document.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
        `#${selector}`,
      ));
  if (el) {
    el.value = value;
    // change → para <select>/<input type=radio|checkbox>
    el.dispatchEvent(new Event('change', { bubbles: true }));
    el.dispatchEvent(new Event('input', { bubbles: true }));
  }
}

function parseBrDate(s: string): Date | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  return new Date(Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd)));
}

function somarDiaIso(d: Date | null, dias: number): string {
  if (!d) return '';
  const novo = new Date(d.getTime() + dias * 24 * 60 * 60 * 1000);
  return novo.toISOString().slice(0, 10); // YYYY-MM-DD
}

function montarMarkdownItensRenovacao(detalhe: {
  numero: string;
  dataInicio: string;
  dataTermino: string;
  itens: Array<{ descricao: string; quantidade: number; valorUnitario: number }>;
}): string {
  const linhas = detalhe.itens
    .map((it) => `- ${it.descricao} (qtd ${it.quantidade}, R$ ${it.valorUnitario.toFixed(2)})`)
    .join('\n');
  return [
    `## Renovação de contrato Omie ${detalhe.numero}`,
    `Vigência original: ${detalhe.dataInicio} a ${detalhe.dataTermino}`,
    '',
    '## Itens importados do contrato anterior',
    linhas,
    '',
    '_Vendedor: revise os itens e ajuste o reajuste antes de enviar._',
  ].join('\n');
}

/** Tenta extrair o accessToken MSAL do sessionStorage. */
function getAuthTokenFromSession(): string {
  for (const k of Object.keys(sessionStorage)) {
    if (k.includes('accesstoken')) {
      try {
        const v = JSON.parse(sessionStorage.getItem(k) ?? 'null') as { secret?: string } | null;
        if (v?.secret) return v.secret;
      } catch {
        /* ignore */
      }
    }
  }
  throw new Error('Token MSAL não encontrado');
}

function popularFormComProposta(p: Proposta): void {
  if (p.tipo !== 'contrato-recorrente') return;
  const c = p.cliente;

  // Restaura itens HaaS no state — UI é re-renderizada via renderHaasLista() no attachHandlers
  state.haasItens = Array.isArray(p.payload.haasItens) ? [...p.payload.haasItens] : [];
  state.premiumModulosSelecionados = Array.isArray(p.payload.premiumModulos)
    ? [...p.payload.premiumModulos]
    : [];

  // Restaura congelamento de reajuste (#255)
  const crjMeses = (p.payload as { congelarReajusteMeses?: number }).congelarReajusteMeses;
  if (typeof crjMeses === 'number' && crjMeses >= 1) {
    state.congelarReajusteAtivo = true;
    state.congelarReajusteMeses = crjMeses;
  } else {
    state.congelarReajusteAtivo = false;
    state.congelarReajusteMeses = 12;
  }

  // Restaura desconto escalonado (se proposta tinha)
  const desc = p.payload.descontoEscalonado;
  if (desc && Array.isArray(desc.faixas) && desc.faixas.length > 0) {
    state.descontoAtivo = true;
    state.descontoFaixas = desc.faixas.map((f) => ({ ...f }));
    state.descontoAplicarEm = desc.aplicarEm ?? 'mensal';
  } else {
    state.descontoAtivo = false;
    state.descontoFaixas = [];
    state.descontoAplicarEm = 'mensal';
  }

  const setVal = (id: string, value: string | number): void => {
    const el = document.getElementById(id) as
      | HTMLInputElement
      | HTMLSelectElement
      | HTMLTextAreaElement
      | null;
    if (el) el.value = String(value);
  };

  // Cliente
  const doc = c.cnpj ?? c.cpf ?? '';
  setVal('documento', doc);
  setVal('empresa', c.empresa);
  setVal('responsavel', c.responsavel);
  setVal('email', c.email);
  setVal('telefone', c.telefone ?? '');

  // Recursos
  setVal('dispositivos', p.payload.recursos.dispositivos);
  setVal('usuarios', p.payload.recursos.usuarios);
  setVal('servidores', p.payload.recursos.servidores);
  setVal('horasSuporte', p.payload.recursos.horasSuporte);

  // Perfil / Pacote / Fidelidade / Validade
  setVal('perfil', p.payload.ambiente.perfil);
  setVal('pacotePrincipal', p.payload.pacotePrincipal);
  setVal('prazoFidelidade', p.payload.prazoFidelidade);
  setVal('validade', p.validade);

  // Onboarding: aceita formato legado (string) ou novo (objeto dimensional).
  // Pré-marca checkbox + valor + flag editado pra UI reativa funcionar.
  const onb = p.payload.onboarding;
  const $onbCobrar = document.getElementById('onboardingCobrar') as HTMLInputElement | null;
  const $onbValor = document.getElementById('onboardingValor') as HTMLInputElement | null;
  if (typeof onb === 'string') {
    // Legado: traduz pra desligado (none) ou ligado (sem valor explícito)
    if ($onbCobrar) $onbCobrar.checked = onb !== 'none';
    if ($onbValor) {
      $onbValor.value = '';
      delete $onbValor.dataset.userEdited;
    }
  } else if (onb.modo === 'nenhum') {
    if ($onbCobrar) $onbCobrar.checked = false;
    if ($onbValor) {
      $onbValor.value = '';
      delete $onbValor.dataset.userEdited;
    }
  } else {
    if ($onbCobrar) $onbCobrar.checked = true;
    if ($onbValor) {
      $onbValor.value = onb.valorFinal.toFixed(2);
      // marca como editado só se de fato diferir da sugestão original
      if (onb.modo === 'manual') $onbValor.dataset.userEdited = 'true';
      else delete $onbValor.dataset.userEdited;
    }
  }

  // Checkbox: pré-marca conforme exibicao.mostrarValoresUnitarios
  const chk = document.getElementById('mostrarValoresUnitarios') as HTMLInputElement | null;
  if (chk) chk.checked = p.exibicao?.mostrarValoresUnitarios !== false;

  // Modo de apresentação dos add-ons
  const modoAddOns = p.exibicao?.modoAddOns === 'completo' ? 'completo' : 'compacto';
  const radioCompacto = document.getElementById('modoAddOnsCompacto') as HTMLInputElement | null;
  const radioCompleto = document.getElementById('modoAddOnsCompleto') as HTMLInputElement | null;
  if (radioCompacto) radioCompacto.checked = modoAddOns === 'compacto';
  if (radioCompleto) radioCompleto.checked = modoAddOns === 'completo';

  // Observações estruturadas: parseia o markdown salvo de volta nos campos
  if (p.observacoes) {
    parsearObservacoesParaForm(p.observacoes);
  }

  // Observações internas (#237) — só admin/equipe vê
  if (p.observacoesInternas) {
    setVal('observacoesInternas', p.observacoesInternas);
  }
}

/**
 * Faz parse reverso do markdown que `montarObservacoesEstruturadas` gera.
 * Popula textarea de problemas, selects de segurança/backup, inventário e observações livres.
 */
function parsearObservacoesParaForm(md: string): void {
  const secoes = md.split(/\n## /).map((s, i) => (i === 0 ? s.replace(/^## /, '') : s));
  const mapa: Record<string, string> = {};
  for (const sec of secoes) {
    const [header, ...body] = sec.split('\n');
    if (!header) continue;
    mapa[header.trim()] = body.join('\n').trim();
  }

  const setVal = (id: string, value: string): void => {
    const el = document.getElementById(id) as
      | HTMLInputElement
      | HTMLSelectElement
      | HTMLTextAreaElement
      | null;
    if (el) el.value = value;
  };

  // Problemas atuais
  if (mapa['Problemas atuais']) {
    setVal('problemasAtuais', mapa['Problemas atuais']);
  }

  // Segurança e Backup — labels devem casar com o que `montarObservacoesEstruturadas` salva:
  //   - Antivírus:  (NÃO "Antivírus gerenciado")
  //   - EDR:
  //   - Backup:     (NÃO "Backup configurado")
  //   - Backup testado:
  const segSec = mapa['Segurança & Backup'] ?? mapa['Seguranca e Backup'];
  if (segSec) {
    const seg = segSec;
    const fromLabel = (label: string): string => {
      // Aceita acentos e múltiplas palavras no valor; trava no fim da linha
      const m = new RegExp(`^-\\s*${label}:\\s*([^\\n]+)$`, 'mi').exec(seg);
      if (!m) return '';
      const v = m[1]!.toLowerCase().trim();
      if (v.startsWith('não sei') || v.startsWith('nao sei')) return 'nsei';
      if (v.startsWith('sim')) return 'sim';
      if (v.startsWith('não') || v.startsWith('nao')) return 'nao';
      return '';
    };
    setVal('segAntivirus', fromLabel('Antivírus') || fromLabel('Antivirus'));
    setVal('segEdr', fromLabel('EDR'));
    setVal('segBackup', fromLabel('Backup'));
    setVal('segBackupTestado', fromLabel('Backup testado'));
  }

  // Inventário — labels devem bater EXATAMENTE com o que o `monter` escreve:
  //   - Impressoras: N    (plural)
  //   - Nobreaks: N       (plural)
  //   - Firewall: N       (SINGULAR)
  //   - Roteador/AP: N    (SINGULAR, sem espaços)
  //   - Switch: N         (SINGULAR)
  const invSec = mapa['Inventário do parque'] ?? mapa['Inventario do parque'];
  if (invSec) {
    const num = (label: string): string => {
      // Escapa caracteres regex no label (ex: '/')
      const esc = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const m = new RegExp(`^-\\s*${esc}:\\s*(\\d+)`, 'mi').exec(invSec);
      return m ? (m[1] ?? '0') : '0';
    };
    setVal('qtdImpressoras', num('Impressoras'));
    setVal('qtdNobreaks', num('Nobreaks'));
    setVal('qtdFirewalls', num('Firewall'));
    setVal('qtdRoteadoresAp', num('Roteador/AP'));
    setVal('qtdSwitches', num('Switch'));
    // DVR removido do template — clonagem ignora valor antigo no markdown.
    setVal('qtdStorages', num('Storage'));
    setVal('qtdOutros', num('Outros'));
  }

  // Add-ons mensais — não tinha parser, por isso o clone vinha todo zerado.
  // Labels seguem `addonsLabels` em montarObservacoesEstruturadas.
  const addSec = mapa['Add-ons mensais'];
  if (addSec) {
    const numA = (label: string): string => {
      const esc = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const m = new RegExp(`^-\\s*${esc}:\\s*(\\d+)`, 'mi').exec(addSec);
      return m ? (m[1] ?? '0') : '0';
    };
    setVal('backupM365', numA('Backup M365'));
    setVal('backupEstacao500gb', numA('Backup Estação 500GB'));
    setVal('backupServidorFisico2tb', numA('Backup Servidor Físico 2TB'));
    setVal('backupServidorVirtual2tb', numA('Backup Servidor Virtual 2TB'));
    setVal('backupPerGb', numA('Backup por GB'));
    setVal('backupTesteRestore', numA('Teste de Restore'));
    setVal('draas', numA('DRaaS'));
    setVal('avGerenciado', numA('AV Gerenciado'));
    setVal('avEdr', numA('AV EDR'));
    setVal('mikrotik5p', numA('Mikrotik 5p'));
    setVal('mikrotikWireless', numA('Mikrotik Wireless'));
    setVal('gestaoFirewallCliente', numA('Gestão Firewall Cliente'));
  }

  // Observações livres
  if (mapa['Observações'] ?? mapa['Observacoes']) {
    setVal('observacoesLivres', mapa['Observações'] ?? mapa['Observacoes'] ?? '');
  }
}

function renderShell(titulo: string): string {
  const btnLabel = state.modoEdicao ? 'Salvar alterações' : 'Criar proposta';
  const cancelHref =
    state.modoEdicao && state.propostaId
      ? `/propostas/${encodeURIComponent(state.propostaId)}`
      : '/propostas';
  return `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--sp-6)">
      <h1 style="font-family:var(--font-display);font-size:var(--fs-3xl);margin:0">${escapeHtml(titulo)}</h1>
      <a href="${cancelHref}" data-route class="btn btn-secondary">Cancelar</a>
    </div>

    <div class="proposta-layout">
      <form id="form-nova-proposta" class="form-card" autocomplete="off" novalidate>
        <div id="top-error" class="badge badge-error" style="display:none;margin-bottom:var(--sp-4)"></div>

        <fieldset class="form-section">
          <label class="form-field" style="flex-direction:row;align-items:center;gap:var(--sp-2)">
            <input type="checkbox" id="modoAvulso" data-recalc />
            <span><strong>Proposta avulsa</strong> — vender só add-ons / HaaS, sem pacote de gestão base</span>
          </label>
        </fieldset>

        ${renderSecaoCliente()}
        ${renderSecaoDiagnostico()}
        ${renderSecaoSegurancaBackup()}
        ${renderSecaoPerfilPlano()}
        ${renderSecaoPremiumModulos()}
        ${renderSecaoRecursos()}
        ${renderSecaoInventario()}
        ${renderSecaoAddons()}
        ${renderSecaoHaas()}
        ${renderSecaoOnboarding()}
        ${renderSecaoDesconto()}

        <div style="display:flex;justify-content:flex-end;gap:var(--sp-3);margin-top:var(--sp-6)">
          <a href="${cancelHref}" data-route class="btn btn-secondary">Cancelar</a>
          <button type="submit" id="btn-submit" class="btn btn-primary">${btnLabel}</button>
        </div>
      </form>

      <aside class="pricing-sidebar">
        <div id="pricing-panel">
          <div style="color:var(--color-text-muted);font-size:var(--fs-sm);text-align:center;padding:var(--sp-4)">
            Carregando tabela…
          </div>
        </div>
      </aside>
    </div>
  `;
}

function renderSecaoCliente(): string {
  return `
    <fieldset class="form-section">
      <legend>1. Cliente</legend>
      <div class="form-row">
        <label class="form-field">
          <span>CNPJ ou CPF</span>
          <div style="display:flex;gap:var(--sp-2)">
            <input type="text" name="documento" id="documento"
              placeholder="com ou sem pontuação" maxlength="20" autocomplete="off" />
            <button type="button" id="btn-omie" class="btn btn-secondary" style="white-space:nowrap">
              Buscar Omie
            </button>
          </div>
          <small id="documento-help" style="color:var(--color-text-muted)">
            Digite CNPJ (14 dígitos) ou CPF (11) — pontuação opcional.
          </small>
        </label>
      </div>
      <div class="form-row form-row-2">
        <label class="form-field"><span>Empresa *</span>
          <input type="text" name="empresa" id="empresa" required maxlength="200" /></label>
        <label class="form-field"><span>Responsável *</span>
          <input type="text" name="responsavel" id="responsavel" required maxlength="200" /></label>
      </div>
      <div class="form-row form-row-2">
        <label class="form-field"><span>E-mail(s) *</span>
          <input type="text" name="email" id="email" required
            placeholder="cliente@empresa.com (vírgula separa múltiplos)" />
          <small class="form-hint">Vários: separe por vírgula, ponto-e-vírgula ou espaço. Todos recebem a proposta.</small></label>
        <label class="form-field"><span>Telefone</span>
          <input type="text" name="telefone" id="telefone"
            placeholder="DDD + número (10 ou 11 dígitos)" maxlength="11" /></label>
      </div>
    </fieldset>
  `;
}

function renderSecaoDiagnostico(): string {
  return `
    <fieldset class="form-section">
      <legend>2. Diagnóstico</legend>
      <p style="color:var(--color-text-muted);font-size:var(--fs-sm);margin:0 0 var(--sp-3) 0">
        Marque o que se aplica. As respostas alimentam "Problemas atuais" — editável.
      </p>
      <div class="diag-grid">
        <label class="diag-check"><input type="checkbox" data-diag-injetar="Tempo de resposta lento do TI atual" /><span>Tempo de resposta ruim do TI atual</span></label>
        <label class="diag-check"><input type="checkbox" data-diag-injetar="Incidentes recorrentes / parada não planejada" /><span>Incidentes recorrentes / parada não planejada</span></label>
        <label class="diag-check"><input type="checkbox" data-diag-injetar="Falta de visibilidade do ambiente (sem monitoramento contínuo)" /><span>Falta de visibilidade do ambiente</span></label>
        <label class="diag-check"><input type="checkbox" data-diag-injetar="Sem backup ou backup nunca testado" /><span>Sem backup ou backup não testado</span></label>
      </div>
      <label class="form-field" style="margin-top:var(--sp-3)">
        <span>Problemas atuais (livre — edite à vontade)</span>
        <textarea name="problemasAtuais" id="problemasAtuais" rows="4" maxlength="2000"
          placeholder="Use bullets (- ) por linha. Checkboxes acima adicionam linhas aqui."></textarea>
      </label>
    </fieldset>
  `;
}

function renderSecaoSegurancaBackup(): string {
  const sel = (name: string, label: string): string => `
    <label class="form-field">
      <span>${label}</span>
      <select name="${name}" id="${name}">
        <option value="">—</option><option value="sim">Sim</option>
        <option value="nao">Não</option><option value="nsei">Não sei</option>
      </select>
    </label>`;
  return `
    <fieldset class="form-section">
      <legend>3. Segurança &amp; Backup</legend>
      <div class="form-row form-row-2">
        ${sel('segAntivirus', 'Antivírus gerenciado hoje?')}
        ${sel('segEdr', 'EDR (Endpoint Detection &amp; Response)?')}
      </div>
      <div class="form-row form-row-2">
        ${sel('segBackup', 'Backup configurado?')}
        ${sel('segBackupTestado', 'Backup testado nos últimos 90 dias?')}
      </div>
    </fieldset>
  `;
}

function renderSecaoRecursos(): string {
  return `
    <fieldset class="form-section avulso-hide">
      <legend>5. Estações</legend>
      <div class="form-row form-row-4">
        <label class="form-field"><span>Estações *</span>
          <input type="number" name="dispositivos" id="dispositivos" required min="0" value="10" data-recalc /></label>
        <label class="form-field"><span>Usuário Microsoft 365 *</span>
          <input type="number" name="usuarios" id="usuarios" required min="0" value="5" data-recalc /></label>
        <label class="form-field"><span>Servidores</span>
          <input type="number" name="servidores" id="servidores" min="0" value="0" data-recalc /></label>
        <label class="form-field" id="horas-suporte-field"><span>Horas suporte/mês <small style="color:var(--color-text-muted)">(Essencial)</small></span>
          <input type="number" name="horasSuporte" id="horasSuporte" min="0" step="0.5" value="2" data-recalc /></label>
      </div>
    </fieldset>
  `;
}

function renderSecaoInventario(): string {
  return `
    <fieldset class="form-section avulso-hide">
      <legend>6. Dispositivos do parque (inventário)</legend>
      <p style="color:var(--color-text-muted);font-size:var(--fs-sm);margin:0 0 var(--sp-3) 0">
        Quantidade aproximada — vira inventário na proposta. Deixe 0 se não houver.
      </p>
      <div class="form-row form-row-4">
        <label class="form-field"><span>Impressoras</span>
          <input type="number" name="qtdImpressoras" id="qtdImpressoras" min="0" value="0" data-recalc /></label>
        <label class="form-field"><span>Nobreaks</span>
          <input type="number" name="qtdNobreaks" id="qtdNobreaks" min="0" value="0" data-recalc /></label>
        <label class="form-field"><span>Firewalls</span>
          <input type="number" name="qtdFirewalls" id="qtdFirewalls" min="0" value="0" data-recalc /></label>
        <label class="form-field"><span>Roteadores / AP</span>
          <input type="number" name="qtdRoteadoresAp" id="qtdRoteadoresAp" min="0" value="0" data-recalc /></label>
      </div>
      <div class="form-row form-row-4">
        <label class="form-field"><span>Switches</span>
          <input type="number" name="qtdSwitches" id="qtdSwitches" min="0" value="0" data-recalc /></label>
        <label class="form-field"><span>Storages (NAS/SAN)</span>
          <input type="number" name="qtdStorages" id="qtdStorages" min="0" value="0" data-recalc /></label>
        <label class="form-field"><span>Outros</span>
          <input type="number" name="qtdOutros" id="qtdOutros" min="0" value="0" data-recalc /></label>
        <!-- DVR / NVR removidos do template MSP — produto comercial separado (proposta projeto-cftv dedicada) -->
      </div>
    </fieldset>
  `;
}

// ─── Seção HaaS — Hardware como Serviço (Adendo HaaS v.1.1) ──────────────

const HAAS_TIPO_LABEL: Record<HaasItemTipo, string> = {
  mikrotik: 'Mikrotik',
  mikrotikWireless: 'Mikrotik Wireless',
  accessPoint: 'Access Point',
  switch: 'Switch',
  nobreak: 'Nobreak',
};

/** Pega defaults do tipo na tabela HaaS vigente. */
function getHaasDefaults(tipo: HaasItemTipo): PrecosHaasItem | null {
  if (!state.tabela?.venda?.haas) return null;
  return state.tabela.venda.haas[tipo];
}

/** Adiciona item HaaS ao state com defaults da tabela. */
function adicionarHaasItem(tipo: HaasItemTipo): void {
  const def = getHaasDefaults(tipo);
  state.haasItens.push({
    tipo,
    modelo: def?.modeloPadrao ?? '',
    quantidade: def?.quantidadePadrao ?? 1,
    valorMensalUnit: def?.valorMensalPadrao ?? 0,
    precoVendaUnit: def?.precoVendaPadrao ?? 0,
    pctHardware: def?.pctHardwarePadrao ?? 70,
  });
  renderHaasLista();
  recalcularPricing();
}

function removerHaasItem(idx: number): void {
  state.haasItens.splice(idx, 1);
  renderHaasLista();
  recalcularPricing();
}

function restaurarHaasItem(idx: number): void {
  const item = state.haasItens[idx];
  if (!item) return;
  const def = getHaasDefaults(item.tipo);
  if (!def) return;
  state.haasItens[idx] = {
    tipo: item.tipo,
    modelo: def.modeloPadrao,
    quantidade: def.quantidadePadrao,
    valorMensalUnit: def.valorMensalPadrao,
    precoVendaUnit: def.precoVendaPadrao,
    pctHardware: def.pctHardwarePadrao,
  };
  renderHaasLista();
  recalcularPricing();
}

/** Retorna { totalMensal, totalVenda } somando qtd × valor unitário de todos os itens. */
function calcularSubtotalHaasLocal(): { totalMensal: number; totalVenda: number } {
  let totalMensal = 0;
  let totalVenda = 0;
  for (const it of state.haasItens) {
    totalMensal += it.quantidade * it.valorMensalUnit;
    totalVenda += it.quantidade * it.precoVendaUnit;
  }
  return { totalMensal, totalVenda };
}

/** Compara campo com default da tabela — usado pra marcar visualmente override. */
function isHaasOverride(item: PropostaHaasItem, campo: keyof PropostaHaasItem): boolean {
  const def = getHaasDefaults(item.tipo);
  if (!def) return false;
  if (campo === 'modelo') return item.modelo !== def.modeloPadrao;
  if (campo === 'quantidade') return item.quantidade !== def.quantidadePadrao;
  if (campo === 'valorMensalUnit') return item.valorMensalUnit !== def.valorMensalPadrao;
  if (campo === 'precoVendaUnit') return item.precoVendaUnit !== def.precoVendaPadrao;
  if (campo === 'pctHardware') return item.pctHardware !== def.pctHardwarePadrao;
  return false;
}

/** Re-renderiza a lista de itens HaaS dentro do container #haas-itens-lista. */
function renderHaasLista(): void {
  const $lista = document.getElementById('haas-itens-lista');
  const $sub = document.getElementById('haas-subtotal');
  if (!$lista) return;

  if (state.haasItens.length === 0) {
    $lista.innerHTML = `
      <div style="color:var(--color-text-muted);font-size:var(--fs-sm);font-style:italic;padding:var(--sp-3) 0">
        Nenhum item HaaS contratado. Cliente recebe apenas o Contrato base (sem Adendo HaaS).
      </div>
    `;
    if ($sub) $sub.innerHTML = '';
    return;
  }

  const ov = (item: PropostaHaasItem, campo: keyof PropostaHaasItem): string =>
    isHaasOverride(item, campo) ? ' overridden' : '';

  const rows = state.haasItens
    .map((item, idx) => {
      const sub = item.quantidade * item.valorMensalUnit;
      const venda = item.quantidade * item.precoVendaUnit;
      return `
        <div class="haas-item-row" data-haas-idx="${idx}">
          <span class="haas-label">${escapeHtml(HAAS_TIPO_LABEL[item.tipo])}</span>
          <input type="text" maxlength="200" value="${escapeHtml(item.modelo)}"
            class="${ov(item, 'modelo')}" data-haas-field="modelo" />
          <input type="number" min="1" max="9999" value="${item.quantidade}"
            class="${ov(item, 'quantidade')}" data-haas-field="quantidade" />
          <input type="number" step="0.01" min="0" value="${item.valorMensalUnit}"
            class="${ov(item, 'valorMensalUnit')}" data-haas-field="valorMensalUnit"
            title="Mensal por unidade" />
          <input type="number" step="0.01" min="0" value="${item.precoVendaUnit}"
            class="${ov(item, 'precoVendaUnit')}" data-haas-field="precoVendaUnit"
            title="Preço de venda por unidade (interno)" />
          <input type="number" step="1" min="0" max="100" value="${item.pctHardware}"
            class="${ov(item, 'pctHardware')}" data-haas-field="pctHardware"
            title="% hardware (split ISS)" />
          <span class="haas-sub">
            ${sub.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mês<br>
            ${venda.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} venda
          </span>
          <button type="button" class="btn-remove" data-haas-remove="${idx}"
            title="Remover item">×</button>
        </div>
      `;
    })
    .join('');

  $lista.innerHTML = `
    <div class="haas-header">
      <span>Tipo</span>
      <span>Modelo</span>
      <span>Qtd</span>
      <span>Mensal/un</span>
      <span>Venda/un</span>
      <span>%HW</span>
      <span>Subtotal</span>
      <span></span>
    </div>
    ${rows}
  `;

  // Subtotal geral + botões "Restaurar padrão" inline
  const { totalMensal, totalVenda } = calcularSubtotalHaasLocal();
  if ($sub) {
    $sub.innerHTML = `
      <strong>Subtotal HaaS:</strong>
      R$ ${totalMensal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/mês recorrente ·
      R$ ${totalVenda.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} valor total dos equipamentos
    `;
  }

  // Wire dos inputs e botões
  $lista.querySelectorAll<HTMLInputElement>('input[data-haas-field]').forEach((inp) => {
    inp.addEventListener('input', () => {
      const row = inp.closest<HTMLElement>('[data-haas-idx]');
      if (!row) return;
      const idx = Number(row.dataset.haasIdx);
      const item = state.haasItens[idx];
      if (!item) return;
      const campo = inp.dataset.haasField as keyof PropostaHaasItem;
      if (campo === 'modelo') {
        item.modelo = inp.value;
      } else {
        const n = Number(inp.value);
        if (Number.isFinite(n)) {
          (item as unknown as Record<string, number>)[campo] = n;
        }
      }
      // Re-renderiza só pra atualizar subtotal + overrides
      renderHaasLista();
      recalcularPricing();
    });
  });
  $lista.querySelectorAll<HTMLButtonElement>('button[data-haas-remove]').forEach((btn) => {
    btn.addEventListener('click', () => {
      removerHaasItem(Number(btn.dataset.haasRemove));
    });
  });
}

/** Wire dos botões "+ Adicionar X" da seção HaaS. Chamado no attachHandlers. */
function wireHaasAddBar(): void {
  document.querySelectorAll<HTMLButtonElement>('[data-add-haas]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const tipo = btn.dataset.addHaas as HaasItemTipo | undefined;
      if (tipo) adicionarHaasItem(tipo);
    });
  });
}
// Silencia "declared but not used" — wireHaasAddBar é chamado de attachHandlers.
void restaurarHaasItem;

function renderSecaoHaas(): string {
  // Não renderiza linhas no SSR — UI é totalmente dinâmica (state.haasItens + addItem/removeItem).
  // Dropdown de tipos vira botões "+ Adicionar X".
  return `
    <fieldset class="form-section form-section-haas">
      <legend>8. HaaS — Hardware como Serviço</legend>
      <p style="color:var(--color-text-muted);font-size:var(--fs-sm);margin:0 0 var(--sp-3) 0">
        Equipamentos fornecidos em <strong>cessão de uso</strong>. Pelo menos 1 item ≥1 unidade
        com valor mensal &gt;0 dispara o <strong>Adendo HaaS</strong> automaticamente no contrato.
        Sem itens HaaS = apenas Contrato base (sem Adendo).
      </p>
      <div id="haas-itens-lista"></div>
      <div class="haas-addbar" style="display:flex;gap:var(--sp-2);flex-wrap:wrap;margin-top:var(--sp-3)">
        <button type="button" class="btn btn-secondary btn-sm" data-add-haas="mikrotik">+ Mikrotik</button>
        <button type="button" class="btn btn-secondary btn-sm" data-add-haas="mikrotikWireless">+ Mikrotik Wireless</button>
        <button type="button" class="btn btn-secondary btn-sm" data-add-haas="accessPoint">+ Access Point</button>
        <button type="button" class="btn btn-secondary btn-sm" data-add-haas="switch">+ Switch</button>
        <button type="button" class="btn btn-secondary btn-sm" data-add-haas="nobreak">+ Nobreak</button>
      </div>
      <div id="haas-subtotal" style="margin-top:var(--sp-3);padding-top:var(--sp-3);border-top:1px solid var(--color-border);font-size:var(--fs-sm);color:var(--color-text-secondary);text-align:right"></div>
    </fieldset>
    <style>
      .haas-item-row,
      .haas-header {
        display: grid;
        grid-template-columns: 110px minmax(0, 1fr) 50px 80px 80px 50px minmax(0, 1.1fr) 28px;
        gap: 6px;
        align-items: center;
      }
      .haas-item-row { padding: 5px 0; }
      .haas-item-row > .haas-label {
        font-weight: 600; font-size: var(--fs-sm);
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }
      .haas-item-row > input {
        padding: 6px 6px; border-radius: 4px;
        border: 1px solid var(--color-border); font-size: var(--fs-sm);
        min-width: 0; /* permite shrink dentro do grid */
      }
      .haas-item-row > input.overridden { border-color: #d4a017; background: rgba(212, 160, 23, 0.06); }
      .haas-item-row > .haas-sub {
        font-size: 10px; color: var(--color-text-secondary);
        line-height: 1.25; text-align: right;
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }
      .haas-item-row > .btn-remove {
        background: transparent; border: 0; cursor: pointer;
        color: var(--color-danger, #c33); font-size: var(--fs-lg); padding: 0;
      }
      .haas-header {
        font-size: 10px; font-weight: 700; text-transform: uppercase;
        letter-spacing: 0.3px; color: var(--color-text-secondary);
        padding: 0 4px 6px 4px;
        border-bottom: 1px solid var(--color-border);
      }
      /* Em telas mais estreitas que o sidebar ocupa, scroll horizontal preserva
         a usabilidade sem quebrar a tabela em linhas múltiplas. */
      #haas-itens-lista { overflow-x: auto; }
      .haas-item-row, .haas-header { min-width: 620px; }
    </style>
  `;
}

// ─── Seção Módulos Premium (Contrato v2026.08, cláusulas 2.5/2.6) ────────

function getCatalogoPremiumAtivo(): NonNullable<
  TabelaPrecosCompleta['venda']['premiumModulos']
>['itens'] {
  return state.tabela?.venda?.premiumModulos?.itens?.filter((m) => m.ativo) ?? [];
}

function isPremiumSelecionado(id: string): boolean {
  return state.premiumModulosSelecionados.some((s) => s.id === id);
}

function togglePremiumModulo(id: string, checked: boolean): void {
  const cat = state.tabela?.venda?.premiumModulos?.itens?.find((m) => m.id === id);
  if (!cat) return;
  if (checked) {
    if (!isPremiumSelecionado(id)) {
      const estacoes = getEstacoesAtuais();
      const horas = horasPremiumModuloPorEstacoes(estacoes);
      const valorHora = cat.valorHoraPadrao;
      state.premiumModulosSelecionados.push({
        id: cat.id,
        nome: cat.nome,
        escopoResumido: cat.escopoResumido,
        valorHora,
        horas,
        valorMensal: valorHora * horas,
      });
    }
  } else {
    state.premiumModulosSelecionados = state.premiumModulosSelecionados.filter((s) => s.id !== id);
  }
  renderPremiumModulosLista();
  recalcularPricing();
}

/** Helper: pega a quantidade atual de estações do form. */
function getEstacoesAtuais(): number {
  const el = document.getElementById('dispositivos') as HTMLInputElement | null;
  return Math.max(0, Math.floor(Number(el?.value) || 0));
}

/** Recalcula horas e valor mensal de todos módulos selecionados quando estações mudam. */
function recalcularPremiumModulos(): void {
  if (state.premiumModulosSelecionados.length === 0) return;
  const estacoes = getEstacoesAtuais();
  const horas = horasPremiumModuloPorEstacoes(estacoes);
  let mudou = false;
  for (const s of state.premiumModulosSelecionados) {
    if (s.horas !== horas) {
      s.horas = horas;
      s.valorMensal = s.valorHora * horas;
      mudou = true;
    }
  }
  if (mudou) renderPremiumModulosLista();
}

function calcularSubtotalPremiumLocal(): {
  somaItens: number;
  combo: number;
  total: number;
  todosMarcados: boolean;
} {
  const catAtivos = getCatalogoPremiumAtivo();
  const comboPct = state.tabela?.venda?.premiumModulos?.comboPct ?? 0;
  const somaItens = state.premiumModulosSelecionados.reduce((a, s) => a + s.valorMensal, 0);
  const todosMarcados = catAtivos.length > 0 && catAtivos.every((m) => isPremiumSelecionado(m.id));
  const combo = todosMarcados ? (somaItens * comboPct) / 100 : 0;
  return { somaItens, combo, total: somaItens - combo, todosMarcados };
}

function renderPremiumModulosLista(): void {
  const $lista = document.getElementById('premium-modulos-lista');
  if (!$lista) return;
  const catAtivos = getCatalogoPremiumAtivo();

  if (catAtivos.length === 0) {
    $lista.innerHTML = `
      <div style="color:var(--color-text-muted);font-size:var(--fs-sm);font-style:italic;padding:var(--sp-3) 0">
        Nenhum módulo cadastrado no catálogo. Configure em /admin/preços.
      </div>
    `;
    renderPremiumSubtotal();
    return;
  }

  const estacoes = getEstacoesAtuais();
  const horas = horasPremiumModuloPorEstacoes(estacoes);
  const fmt = (n: number): string =>
    n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  $lista.innerHTML = catAtivos
    .map((mod) => {
      const checked = isPremiumSelecionado(mod.id);
      const valorHora = mod.valorHoraPadrao;
      const valorMensal = valorHora * horas;
      return `
        <div class="pm-row${checked ? ' pm-row-on' : ''}" data-pm-id="${escapeHtml(mod.id)}">
          <label class="pm-toggle">
            <input type="checkbox" data-pm-toggle ${checked ? 'checked' : ''} />
            <strong>${escapeHtml(mod.nome)}</strong>
          </label>
          <div class="pm-preview">${escapeHtml(mod.escopoResumido)}</div>
          <div class="pm-calculo">
            <strong>${horas}h × R$ ${fmt(valorHora)}/h = R$ ${fmt(valorMensal)}/mês</strong>
            <small style="display:block;color:var(--color-text-muted);font-size:10px;font-style:italic;margin-top:2px;">
              Horas calculadas pela regra de ${estacoes} estação(ões). Edite valor/hora em /admin/preços.
            </small>
          </div>
        </div>
      `;
    })
    .join('');

  $lista.querySelectorAll<HTMLElement>('.pm-row').forEach((row) => {
    const id = row.dataset.pmId;
    if (!id) return;
    row.querySelector<HTMLInputElement>('[data-pm-toggle]')?.addEventListener('change', (ev) => {
      togglePremiumModulo(id, (ev.target as HTMLInputElement).checked);
    });
  });

  renderPremiumSubtotal();
}

function renderPremiumSubtotal(): void {
  const $sub = document.getElementById('premium-modulos-subtotal');
  if (!$sub) return;
  if (state.premiumModulosSelecionados.length === 0) {
    $sub.innerHTML = '';
    return;
  }
  const { somaItens, combo, total, todosMarcados } = calcularSubtotalPremiumLocal();
  const fmt = (n: number): string =>
    n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  $sub.innerHTML = `
    Soma dos módulos: <strong>R$ ${fmt(somaItens)}/mês</strong>
    ${todosMarcados ? `<br><span style="color:#1F4E79">Combo (todos marcados — ${state.tabela?.venda?.premiumModulos?.comboPct ?? 0}%): −R$ ${fmt(combo)}</span>` : ''}
    <br><strong>Total módulos: R$ ${fmt(total)}/mês</strong>
  `;
}

function atualizarVisibilidadePremium(): void {
  const $sec = document.getElementById('secao-premium-modulos');
  if (!$sec) return;
  const $pac = document.getElementById('pacotePrincipal') as HTMLSelectElement | null;
  const ativo = $pac?.value === 'premium';
  $sec.style.display = ativo ? '' : 'none';
  if (!ativo && state.premiumModulosSelecionados.length > 0) {
    state.premiumModulosSelecionados = [];
    recalcularPricing();
  }
}

function renderSecaoPremiumModulos(): string {
  return `
    <fieldset class="form-section form-section-premium" id="secao-premium-modulos" style="display:none">
      <legend>4.1 Módulos Premium <span class="pm-badge-mini">v2026.08</span></legend>
      <p style="color:var(--color-text-muted);font-size:var(--fs-sm);margin:0 0 var(--sp-3) 0">
        Módulos opcionais empilháveis exclusivos do plano <strong>Premium</strong>. Combo aplicado
        quando TODOS os módulos ativos do catálogo estão marcados (Contrato v.2026.08, cláusulas 2.5/2.6).
      </p>
      <div id="premium-modulos-lista"></div>
      <div id="premium-modulos-subtotal" style="margin-top:var(--sp-3);padding-top:var(--sp-3);border-top:1px solid var(--color-border);font-size:var(--fs-sm);color:var(--color-text-secondary);text-align:right;line-height:1.6"></div>
    </fieldset>
    <style>
      .pm-badge-mini { font-size:10px;font-weight:600;color:var(--color-primary);background:rgba(31,78,121,0.08);padding:2px 6px;border-radius:4px;margin-left:6px; }
      .pm-row { display:grid;grid-template-columns:1fr auto;gap:var(--sp-2) var(--sp-3);align-items:start;padding:var(--sp-2) var(--sp-3);border:1px solid var(--color-border);border-radius:6px;margin-bottom:var(--sp-2);transition:border-color 0.15s; }
      .pm-row-on { border-color:var(--color-primary);background:rgba(31,78,121,0.03); }
      .pm-toggle { display:flex;align-items:center;gap:8px;cursor:pointer;font-size:var(--fs-sm);grid-column:1 / -1; }
      .pm-toggle input { width:18px;height:18px; }
      .pm-preview { font-size:var(--fs-xs);color:var(--color-text-muted);font-style:italic;padding-left:26px;line-height:1.4;grid-column:1 / -1; }
      .pm-row textarea { grid-column:1;padding:8px;border-radius:4px;border:1px solid var(--color-border);font-size:var(--fs-sm);font-family:inherit;resize:vertical;min-height:50px; }
      .pm-valor { grid-column:2;align-self:center;font-size:var(--fs-sm);white-space:nowrap; }
      .pm-valor input { width:100px;padding:6px 8px;border-radius:4px;border:1px solid var(--color-border);font-size:var(--fs-sm);text-align:right; }
    </style>
  `;
}

// ─── Seção Desconto Promocional Escalonado (até 4 faixas) ────────────

function renderSecaoDesconto(): string {
  return `
    <fieldset class="form-section form-section-desconto">
      <legend>10. Desconto promocional</legend>
      <p style="color:var(--color-text-muted);font-size:var(--fs-sm);margin:0 0 var(--sp-3) 0">
        Aplica desconto temporal no Mensal Recorrente. Configure faixas (ex: 15% nos 3 primeiros meses,
        depois 10% por mais 3 meses). Após a última faixa, retorna ao valor cheio até o fim do contrato.
      </p>
      <label class="dsc-toggle">
        <input type="checkbox" id="dsc-ativo" data-recalc />
        <span>Aplicar desconto promocional?</span>
      </label>
      <div id="dsc-config" style="display:none;margin-top:var(--sp-3)">
        <div style="display:flex;gap:var(--sp-3);align-items:center;margin-bottom:var(--sp-3)">
          <label style="display:flex;align-items:center;gap:6px;font-size:var(--fs-sm)">
            <input type="radio" name="dsc-aplicar" value="mensal" id="dsc-aplicar-mensal" data-recalc />
            Aplicar apenas no Mensal Recorrente
          </label>
          <label style="display:flex;align-items:center;gap:6px;font-size:var(--fs-sm)">
            <input type="radio" name="dsc-aplicar" value="mensal-onboarding" id="dsc-aplicar-onb" data-recalc />
            Aplicar no Mensal + Onboarding (parcela)
          </label>
        </div>
        <div id="dsc-faixas-lista"></div>
        <button type="button" class="btn btn-secondary" id="dsc-add-faixa" style="margin-top:var(--sp-2)">
          + Adicionar faixa
        </button>
        <div id="dsc-cronograma" style="margin-top:var(--sp-3);padding:var(--sp-3);background:rgba(31,78,121,0.04);border-left:3px solid var(--color-primary);border-radius:4px;font-size:var(--fs-sm)"></div>
      </div>
      <hr style="margin:var(--sp-4) 0;border:none;border-top:1px dashed var(--color-border)" />
      <label class="dsc-toggle" style="margin-top:var(--sp-2)">
        <input type="checkbox" id="crj-ativo" />
        <span>Congelar reajuste IPCA por um período?</span>
      </label>
      <div id="crj-config" style="display:none;margin-top:var(--sp-3);padding:var(--sp-3);background:rgba(212,160,23,0.06);border-left:3px solid var(--color-primary);border-radius:4px">
        <p style="margin:0 0 var(--sp-2) 0;color:var(--color-text-muted);font-size:var(--fs-sm)">
          Marcador comercial. Não altera o valor mensal — apenas suspende a aplicação
          do reajuste anual de IPCA pelo período informado. Vai pro contrato como cláusula extra.
        </p>
        <label style="display:inline-flex;align-items:center;gap:var(--sp-2);font-size:var(--fs-sm)">
          Período do congelamento:
          <input type="number" id="crj-meses" min="1" max="36" step="1" value="12" style="width:80px;padding:6px 8px;border-radius:4px;border:1px solid var(--color-border)" />
          meses
        </label>
      </div>
    </fieldset>
    <style>
      .dsc-toggle { display:flex;align-items:center;gap:8px;cursor:pointer;font-size:var(--fs-sm); }
      .dsc-toggle input { width:18px;height:18px; }
      .dsc-faixa { display:grid;grid-template-columns:140px 120px 120px 1fr auto;gap:var(--sp-2);padding:var(--sp-2) var(--sp-3);border:1px solid var(--color-border);border-radius:6px;margin-bottom:var(--sp-2);align-items:center; }
      .dsc-faixa select, .dsc-faixa input { padding:6px 8px;border-radius:4px;border:1px solid var(--color-border);font-size:var(--fs-sm);width:100%; }
      .dsc-faixa label { font-size:11px;color:var(--color-text-muted); }
      .dsc-faixa-num { font-weight:600;font-size:var(--fs-xs);color:var(--color-primary);background:rgba(31,78,121,0.1);padding:2px 8px;border-radius:10px; }
      .dsc-remover { background:transparent;border:1px solid var(--color-border);color:#c00;padding:6px 10px;border-radius:4px;cursor:pointer;font-size:var(--fs-xs); }
      .dsc-cron-linha { padding:4px 0;border-bottom:1px dashed var(--color-border); }
      .dsc-cron-linha:last-child { border-bottom:none; }
      .dsc-cron-resumo { margin-top:var(--sp-2);font-weight:600;color:var(--color-primary); }
    </style>
  `;
}

function renderDescontoFaixaRow(idx: number): string {
  const f = state.descontoFaixas[idx];
  if (!f) return '';
  return `
    <div class="dsc-faixa" data-faixa-idx="${idx}">
      <div>
        <label>Tipo</label>
        <select data-dsc-field="tipo" data-recalc>
          <option value="pct" ${f.tipo === 'pct' ? 'selected' : ''}>%</option>
          <option value="valor" ${f.tipo === 'valor' ? 'selected' : ''}>R$</option>
        </select>
      </div>
      <div>
        <label>Valor</label>
        <input type="number" min="0.01" step="0.01" value="${f.valor}" data-dsc-field="valor" data-recalc />
      </div>
      <div>
        <label>Duração (meses)</label>
        <input type="number" min="1" max="36" step="1" value="${f.duracaoMeses}" data-dsc-field="duracaoMeses" data-recalc />
      </div>
      <div>
        <label>Motivo (opcional)</label>
        <input type="text" maxlength="200" value="${escapeHtml(f.motivo ?? '')}" data-dsc-field="motivo" placeholder="ex: Campanha lançamento" />
      </div>
      <div>
        <button type="button" class="dsc-remover" data-dsc-remover="${idx}">Remover</button>
      </div>
    </div>
  `;
}

function renderDescontoFaixasLista(): void {
  const $lista = document.getElementById('dsc-faixas-lista');
  if (!$lista) return;
  $lista.innerHTML = state.descontoFaixas.map((_, i) => renderDescontoFaixaRow(i)).join('');
  const $add = document.getElementById('dsc-add-faixa') as HTMLButtonElement | null;
  if ($add) $add.disabled = state.descontoFaixas.length >= 4;
  // Re-attach handlers
  $lista.querySelectorAll('[data-dsc-field]').forEach((el) => {
    el.addEventListener('input', onDescontoFieldChange);
    el.addEventListener('change', onDescontoFieldChange);
  });
  $lista.querySelectorAll('[data-dsc-remover]').forEach((el) => {
    el.addEventListener('click', (e) => {
      const idx = Number((e.currentTarget as HTMLElement).getAttribute('data-dsc-remover'));
      state.descontoFaixas.splice(idx, 1);
      renderDescontoFaixasLista();
      recalcularPricing();
    });
  });
}

function attachDescontoHandlers(): void {
  const $ativo = document.getElementById('dsc-ativo') as HTMLInputElement | null;
  const $add = document.getElementById('dsc-add-faixa') as HTMLButtonElement | null;
  const $rMensal = document.getElementById('dsc-aplicar-mensal') as HTMLInputElement | null;
  const $rOnb = document.getElementById('dsc-aplicar-onb') as HTMLInputElement | null;

  $ativo?.addEventListener('change', () => {
    state.descontoAtivo = $ativo.checked;
    // Se ativando e não tem faixa, cria uma default 10% por 3 meses
    if (state.descontoAtivo && state.descontoFaixas.length === 0) {
      state.descontoFaixas.push({ tipo: 'pct', valor: 10, duracaoMeses: 3 });
    }
    syncDescontoUI();
    renderDescontoFaixasLista();
    recalcularPricing();
  });

  $add?.addEventListener('click', () => {
    if (state.descontoFaixas.length >= 4) return;
    state.descontoFaixas.push({ tipo: 'pct', valor: 5, duracaoMeses: 3 });
    renderDescontoFaixasLista();
    recalcularPricing();
  });

  const onAplicarChange = (): void => {
    if ($rOnb?.checked) state.descontoAplicarEm = 'mensal-onboarding';
    else state.descontoAplicarEm = 'mensal';
    recalcularPricing();
  };
  $rMensal?.addEventListener('change', onAplicarChange);
  $rOnb?.addEventListener('change', onAplicarChange);

  // #255 — Congelar reajuste IPCA (marcador comercial)
  const $crjAtivo = document.getElementById('crj-ativo') as HTMLInputElement | null;
  const $crjMeses = document.getElementById('crj-meses') as HTMLInputElement | null;
  $crjAtivo?.addEventListener('change', () => {
    state.congelarReajusteAtivo = $crjAtivo.checked;
    syncCongelarReajusteUI();
  });
  $crjMeses?.addEventListener('input', () => {
    const n = Number($crjMeses.value);
    if (Number.isFinite(n) && n >= 1 && n <= 36) {
      state.congelarReajusteMeses = Math.floor(n);
    }
  });
}

function syncCongelarReajusteUI(): void {
  const $ativo = document.getElementById('crj-ativo') as HTMLInputElement | null;
  const $config = document.getElementById('crj-config');
  const $meses = document.getElementById('crj-meses') as HTMLInputElement | null;
  if ($ativo) $ativo.checked = state.congelarReajusteAtivo;
  if ($config) $config.style.display = state.congelarReajusteAtivo ? 'block' : 'none';
  if ($meses) $meses.value = String(state.congelarReajusteMeses);
}

function syncDescontoUI(): void {
  const $ativo = document.getElementById('dsc-ativo') as HTMLInputElement | null;
  const $config = document.getElementById('dsc-config');
  const $rMensal = document.getElementById('dsc-aplicar-mensal') as HTMLInputElement | null;
  const $rOnb = document.getElementById('dsc-aplicar-onb') as HTMLInputElement | null;
  if ($ativo) $ativo.checked = state.descontoAtivo;
  if ($config) $config.style.display = state.descontoAtivo ? 'block' : 'none';
  if ($rMensal) $rMensal.checked = state.descontoAplicarEm === 'mensal';
  if ($rOnb) $rOnb.checked = state.descontoAplicarEm === 'mensal-onboarding';
}

function renderCronograma(breakdown: ReturnType<typeof calcularPricing> | null): void {
  const $cron = document.getElementById('dsc-cronograma');
  if (!$cron) return;
  const desc = breakdown?.descontoEscalonado;
  if (!desc || desc.cronograma.length === 0) {
    $cron.innerHTML =
      '<em style="color:var(--color-text-muted)">Preview do cronograma aparece aqui.</em>';
    return;
  }
  const linhas = desc.cronograma
    .map(
      (l: {
        mesInicio: number;
        mesFim: number;
        valorMensal: number;
        tipo: 'pct' | 'valor' | null;
        valorAplicado: number | null;
        motivo?: string;
      }) => {
        const valor = fmtBRL(l.valorMensal);
        const tipo =
          l.tipo === 'pct'
            ? ` (${l.valorAplicado}% off)`
            : l.tipo === 'valor'
              ? ` (-R$ ${l.valorAplicado?.toFixed(2)})`
              : ' (valor cheio)';
        const motivo = l.motivo ? ` — <em>${escapeHtml(l.motivo)}</em>` : '';
        return `<div class="dsc-cron-linha">Mês ${l.mesInicio}${l.mesFim !== l.mesInicio ? `–${l.mesFim}` : ''}: <strong>${valor}/mês</strong>${tipo}${motivo}</div>`;
      },
    )
    .join('');
  $cron.innerHTML = `
    ${linhas}
    <div class="dsc-cron-resumo">
      Total contrato: ${fmtBRL(desc.totalContratoComDesconto)}
      <span style="color:var(--color-text-muted);font-weight:normal">(sem desconto: ${fmtBRL(desc.totalContratoSemDesconto)})</span>
      <br/>Economia total: <strong>${fmtBRL(desc.economiaTotal)}</strong>
    </div>
  `;
}

function onDescontoFieldChange(e: Event): void {
  const el = e.currentTarget as HTMLInputElement | HTMLSelectElement;
  const faixaEl = el.closest('[data-faixa-idx]');
  if (!faixaEl) return;
  const idx = Number(faixaEl.getAttribute('data-faixa-idx'));
  const faixa = state.descontoFaixas[idx];
  if (!faixa) return;
  const field = el.getAttribute('data-dsc-field');
  if (field === 'tipo') faixa.tipo = el.value as 'pct' | 'valor';
  else if (field === 'valor') faixa.valor = Number(el.value) || 0;
  else if (field === 'duracaoMeses') faixa.duracaoMeses = Number(el.value) || 1;
  else if (field === 'motivo') faixa.motivo = el.value.trim() || undefined;
  recalcularPricing();
}

function renderSecaoAddons(): string {
  const inp = (name: string, label: string): string => `
    <label class="form-field addon-field">
      <span>${label}</span>
      <input type="number" name="${name}" id="${name}" min="0" value="0" data-addon data-recalc />
      <small class="addon-unit" data-addon-unit-for="${name}">—</small>
    </label>`;
  // Variante com trava de incremento (step). Usado pelo Backup por GB (50 em 50).
  const inpStep = (name: string, label: string, step: number, hint: string): string => `
    <label class="form-field addon-field">
      <span>${label}</span>
      <input type="number" name="${name}" id="${name}" min="0" step="${step}" value="0"
        data-addon data-recalc data-step-multiple="${step}" />
      <small class="addon-unit" data-addon-unit-for="${name}">—</small>
      <small style="font-size:11px;color:var(--color-text-muted);font-style:italic">${escapeHtml(hint)}</small>
    </label>`;
  return `
    <fieldset class="form-section">
      <legend>7. Add-ons mensais</legend>
      <p style="color:var(--color-text-muted);font-size:var(--fs-sm);margin:0 0 var(--sp-3) 0">
        Quantidade de cada add-on a incluir. O valor unitário aparece abaixo do campo (vem da tabela vigente).
      </p>

      <div class="addon-group-title">Backup Nuvem</div>
      <div class="form-row form-row-4">
        ${inp('backupM365', 'M365 (usuários)')}
        ${inp('backupEstacao500gb', 'Estação 500GB')}
        ${inp('backupServidorFisico2tb', 'Servidor Físico 2TB')}
        ${inp('backupServidorVirtual2tb', 'Servidor Virtual 2TB')}
      </div>
      <div class="form-row form-row-3">
        ${inpStep('backupPerGb', 'GB adicionais', 50, 'Múltiplos de 50 (50, 100, 150…)')}
        ${inp('backupTesteRestore', 'Teste de Restore (qtd)')}
        ${inp('draas', 'DRaaS (por backup de servidor)')}
      </div>

      <div class="addon-group-title">Segurança</div>
      <div class="form-row form-row-3">
        ${inp('avGerenciado', 'AV Gerenciado (usuários)')}
        ${inp('avEdr', 'AV EDR (usuários)')}
        ${inp('gestaoFirewallCliente', 'Gestão Firewall Cliente')}
      </div>
      <!-- Bloco "Firewall" antigo removido. Mikrotik 5p / Mikrotik Wireless agora
           vivem no HaaS (Adendo HaaS v.1.1). Gestão Firewall Cliente movida pra
           Segurança (gestão lógica do firewall do cliente, sem HaaS). -->
    </fieldset>
  `;
}

function renderSecaoPerfilPlano(): string {
  return `
    <fieldset class="form-section avulso-hide">
      <legend>4. Perfil &amp; Plano</legend>
      <div class="form-row form-row-3">
        <label class="form-field"><span>Perfil do ambiente *</span>
          <select name="perfil" id="perfil" required data-recalc>
            <option value="simples">Simples</option>
            <option value="moderado" selected>Moderado</option>
            <option value="critico">Crítico</option>
          </select></label>
        <label class="form-field"><span>Pacote principal *</span>
          <select name="pacotePrincipal" id="pacotePrincipal" required data-recalc>
            <option value="essencial">Essencial</option>
            <option value="professional" selected>Professional</option>
            <option value="premium">Premium</option>
          </select></label>
        <label class="form-field"><span>&nbsp;</span><span style="visibility:hidden">spacer</span></label>
      </div>
      <div class="form-row form-row-2">
        <label class="form-field"><span>Prazo de fidelidade *</span>
          <select name="prazoFidelidade" id="prazoFidelidade" required>
            <option value="12 meses" selected>12 meses</option>
            <option value="24 meses">24 meses</option>
            <option value="36 meses">36 meses</option>
            <option value="Sem fidelidade">Sem fidelidade</option>
          </select></label>
        <label class="form-field"><span>Validade *</span>
          <input type="text" name="validade" id="validade" required value="15 dias úteis" maxlength="50" /></label>
      </div>
    </fieldset>
  `;
}

function renderSecaoOnboarding(): string {
  return `
    <fieldset class="form-section">
      <legend>9. Onboarding &amp; Apresentação</legend>
      <div class="form-field" style="margin-top:0; padding:var(--sp-4); background:var(--color-surface-alt,#fafaf7); border:1px solid var(--color-border); border-radius:6px;">
        <div style="display:flex; align-items:center; justify-content:space-between; gap:var(--sp-3); margin-bottom:var(--sp-2);">
          <strong style="font-size:var(--fs-sm);">Onboarding (cobrado uma vez, parcelado em 6×)</strong>
          <label style="display:flex; align-items:center; gap:6px; font-size:var(--fs-sm); cursor:pointer;">
            <input type="checkbox" name="onboardingCobrar" id="onboardingCobrar" checked data-recalc />
            <span>Cobrar onboarding</span>
          </label>
        </div>
        <div id="onboarding-bloco" style="display:flex; flex-direction:column; gap:var(--sp-2);">
          <div style="display:flex; align-items:center; gap:var(--sp-3);">
            <label class="form-field" style="flex:1;">
              <span>Valor do onboarding (R$)</span>
              <input type="number" name="onboardingValor" id="onboardingValor" min="0" step="0.01"
                placeholder="0,00" data-recalc />
            </label>
            <button type="button" id="onboardingRestaurar"
              style="height:38px; padding:0 var(--sp-3); align-self:flex-end; font-size:var(--fs-xs); background:var(--color-surface); border:1px solid var(--color-border); border-radius:4px; cursor:pointer;">
              Restaurar sugestão
            </button>
          </div>
          <div id="onboardingEditadoAviso"
            style="display:none; font-size:var(--fs-xs); color:#b45309; background:#fffbeb; border-left:3px solid #d97706; padding:6px 10px; border-radius:4px;">
            ⚠ Valor editado manualmente — diferente da sugestão automática.
          </div>
          <details id="onboardingBreakdown" style="font-size:var(--fs-xs); color:var(--color-text-secondary);">
            <summary style="cursor:pointer; user-select:none;">Como o valor sugerido é calculado</summary>
            <div id="onboardingBreakdownConteudo" style="margin-top:6px; padding:var(--sp-2) var(--sp-3); background:#fff; border:1px solid var(--color-border); border-radius:4px; font-family:var(--font-mono, monospace); white-space:pre-line; line-height:1.5;">
              —
            </div>
          </details>
        </div>
      </div>
      <label class="form-field" style="margin-top:var(--sp-3)">
        <span>Observações livres (opcional)</span>
        <textarea name="observacoesLivres" id="observacoesLivres" rows="3" maxlength="2000"
          placeholder="Qualquer contexto adicional não coberto pelas seções acima."></textarea>
      </label>
      <div class="form-field" style="margin-top:var(--sp-3); padding:var(--sp-3); background:#fffaf0; border:2px dashed #d97706; border-radius:6px;">
        <label style="display:block">
          <span style="display:flex;align-items:center;gap:6px;font-weight:600;color:#92400e">
            📝 Observações internas (não enviadas ao cliente)
          </span>
          <span style="display:block;font-size:var(--fs-xs);color:#92400e;margin:4px 0 8px">
            Registre negociações, condições especiais, atenções técnicas. <strong>Cliente NÃO vê</strong>
            esse conteúdo em nenhuma view pública, PDF ou email.
          </span>
          <textarea name="observacoesInternas" id="observacoesInternas" rows="3" maxlength="5000"
            placeholder="Ex: cliente negociou desconto em troca de 24m de fidelidade. Atenção: rede legada precisa upgrade no onboarding."
            style="width:100%; background:#fff"></textarea>
        </label>
      </div>
      <div class="form-field" style="margin-top:var(--sp-3); padding:var(--sp-3); background:var(--color-surface-alt,#fafaf7); border:1px solid var(--color-border); border-radius:6px;">
        <label style="display:flex; align-items:flex-start; gap:var(--sp-2); cursor:pointer;">
          <input type="checkbox" name="mostrarValoresUnitarios" id="mostrarValoresUnitarios"
            style="margin-top:3px; flex:none;" />
          <span style="display:flex; flex-direction:column; gap:2px;">
            <strong style="font-size:var(--fs-sm);">Mostrar valor unitário de cada item na proposta</strong>
            <span style="font-size:var(--fs-xs); color:var(--color-text-muted);">
              Quando desmarcado, o cliente verá apenas o valor total do plano (sem qtd × unitário das estações, servidores e inventário).
              Add-ons sempre aparecem item a item.
            </span>
          </span>
        </label>
      </div>
      <div class="form-field" style="margin-top:var(--sp-3); padding:var(--sp-3); background:var(--color-surface-alt,#fafaf7); border:1px solid var(--color-border); border-radius:6px;">
        <div style="display:flex; flex-direction:column; gap:var(--sp-2);">
          <strong style="font-size:var(--fs-sm);">Apresentação dos add-ons</strong>
          <label style="display:flex; align-items:flex-start; gap:var(--sp-2); cursor:pointer;">
            <input type="radio" name="modoAddOns" value="compacto" id="modoAddOnsCompacto"
              style="margin-top:3px; flex:none;" />
            <span style="display:flex; flex-direction:column; gap:2px;">
              <span style="font-size:var(--fs-sm); font-weight:600;">Compacta</span>
              <span style="font-size:var(--fs-xs); color:var(--color-text-muted);">
                Lista qtd + valor de cada add-on. Use no contrato master de gestão de TI.
              </span>
            </span>
          </label>
          <label style="display:flex; align-items:flex-start; gap:var(--sp-2); cursor:pointer;">
            <input type="radio" name="modoAddOns" value="completo" id="modoAddOnsCompleto" checked
              style="margin-top:3px; flex:none;" />
            <span style="display:flex; flex-direction:column; gap:2px;">
              <span style="font-size:var(--fs-sm); font-weight:600;">Completa (padrão — com descrição de cada produto)</span>
              <span style="font-size:var(--fs-xs); color:var(--color-text-muted);">
                Mostra um card por produto (Cove, Mikrotik, M365, etc) com descrição e funcionalidades.
                Use quando o contrato é só de add-on — backup, firewall, e-mail.
              </span>
            </span>
          </label>
        </div>
      </div>
    </fieldset>
  `;
}

// ─── Handlers ────────────────────────────────────────────────────────────

function attachHandlers(vendedorEmail: string, vendedorNome: string): void {
  const $form = document.getElementById('form-nova-proposta') as HTMLFormElement | null;
  const $btnOmie = document.getElementById('btn-omie') as HTMLButtonElement | null;
  const $doc = document.getElementById('documento') as HTMLInputElement | null;
  if (!$form || !$btnOmie || !$doc) return;

  $btnOmie.addEventListener('click', () => {
    void lookupOmie($doc.value);
  });

  attachDiagnosticoBindings();
  attachRecalcBindings();
  wireHaasAddBar();
  renderHaasLista();
  attachDescontoHandlers();
  syncCongelarReajusteUI();
  renderDescontoFaixasLista();
  // Sincroniza UI com state restaurado (modo edição)
  syncDescontoUI();

  $form.addEventListener('submit', (event) => {
    event.preventDefault();
    void submitForm($form, vendedorEmail, vendedorNome);
  });
}

function attachRecalcBindings(): void {
  document.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-recalc]').forEach((el) => {
    el.addEventListener('input', recalcularPricing);
    el.addEventListener('change', recalcularPricing);
  });

  // Inputs com trava de incremento (data-step-multiple) — ex.: Backup por GB (50/50).
  // No blur, arredonda pra cima até o próximo múltiplo. Se 0, mantém 0.
  document.querySelectorAll<HTMLInputElement>('[data-step-multiple]').forEach((el) => {
    el.addEventListener('blur', () => {
      const step = Number(el.dataset.stepMultiple ?? '1');
      if (!Number.isFinite(step) || step <= 0) return;
      const v = Number(el.value);
      if (!Number.isFinite(v) || v <= 0) {
        el.value = '0';
        recalcularPricing();
        return;
      }
      // Arredonda para o múltiplo mais próximo (ties → pra cima).
      const arred = Math.round(v / step) * step;
      const aplicado = arred > 0 ? arred : step;
      if (aplicado !== v) {
        el.value = String(aplicado);
        recalcularPricing();
      }
    });
  });
  // Auto-sugestão de horas no Essencial: dispara quando muda estações, perfil
  // ou pacote. Só preenche se o usuário ainda não editou manualmente.
  const $disp = document.getElementById('dispositivos') as HTMLInputElement | null;
  const $perfil = document.getElementById('perfil') as HTMLSelectElement | null;
  const $pacote = document.getElementById('pacotePrincipal') as HTMLSelectElement | null;
  const $horas = document.getElementById('horasSuporte') as HTMLInputElement | null;
  const sugerir = (): void => {
    if (!$disp || !$perfil || !$pacote || !$horas) return;
    if ($pacote.value !== 'essencial') return;
    if ($horas.dataset.userEdited === 'true') return;
    const estacoes = Number($disp.value) || 0;
    const perfil = ($perfil.value as 'simples' | 'moderado' | 'critico') || 'moderado';
    const h = sugerirHorasSuporteEssencial(estacoes, perfil);
    $horas.value = String(h);
    recalcularPricing();
  };
  $disp?.addEventListener('input', sugerir);
  $perfil?.addEventListener('change', sugerir);
  $pacote?.addEventListener('change', sugerir);
  // Marca como editado manualmente quando o usuário digita no campo horas
  $horas?.addEventListener('input', () => {
    if ($horas) $horas.dataset.userEdited = 'true';
  });
  // Dispara uma vez no carregamento pra preencher com a sugestão inicial
  sugerir();

  // ── Onboarding dimensional ─────────────────────────────────────────────
  // Marca o campo como "userEdited" quando o usuário digita manualmente,
  // pra parar de sobrescrever com a sugestão a cada recálculo.
  const $onbValor = document.getElementById('onboardingValor') as HTMLInputElement | null;
  $onbValor?.addEventListener('input', () => {
    if ($onbValor) $onbValor.dataset.userEdited = 'true';
  });
  // Botão "Restaurar sugestão" volta ao valor calculado e limpa userEdited
  const $onbRestaurar = document.getElementById('onboardingRestaurar') as HTMLButtonElement | null;
  $onbRestaurar?.addEventListener('click', () => {
    if ($onbValor) {
      delete $onbValor.dataset.userEdited;
      $onbValor.value = '';
    }
    recalcularPricing();
  });
}

function attachDiagnosticoBindings(): void {
  const $textarea = document.getElementById('problemasAtuais') as HTMLTextAreaElement | null;
  if (!$textarea) return;
  document.querySelectorAll<HTMLInputElement>('input[data-diag-injetar]').forEach((cb) => {
    cb.addEventListener('change', () => {
      const texto = cb.dataset.diagInjetar ?? '';
      if (!texto) return;
      const linha = `- ${texto}`;
      const linhas = $textarea.value.split('\n');
      const idx = linhas.findIndex((l) => l.trim() === linha);
      if (cb.checked && idx === -1) {
        if ($textarea.value.trim() === '') {
          $textarea.value = linha;
        } else {
          $textarea.value = `${$textarea.value.replace(/\s+$/, '')}\n${linha}`;
        }
      } else if (!cb.checked && idx !== -1) {
        linhas.splice(idx, 1);
        $textarea.value = linhas
          .join('\n')
          .replace(/\n{3,}/g, '\n\n')
          .trimEnd();
      }
    });
  });
}

// ─── Pricing reativo ─────────────────────────────────────────────────────

function getNum(name: string): number {
  const el = document.getElementById(name) as HTMLInputElement | null;
  if (!el) return 0;
  const n = Number(el.value);
  return Number.isFinite(n) ? n : 0;
}
function getStr(name: string): string {
  const el = document.getElementById(name) as HTMLInputElement | HTMLSelectElement | null;
  return el?.value ?? '';
}

function lerAddons(): AddOns {
  const a: AddOns = { ...ADDONS_VAZIO };
  for (const k of Object.keys(ADDONS_VAZIO) as (keyof AddOns)[]) {
    a[k] = getNum(k);
  }
  return a;
}

function lerInventario(): Inventario {
  return {
    impressoras: getNum('qtdImpressoras'),
    nobreaks: getNum('qtdNobreaks'),
    firewalls: getNum('qtdFirewalls'),
    roteadoresAp: getNum('qtdRoteadoresAp'),
    switches: getNum('qtdSwitches'),
    // DVR/NVR removidos do template MSP — sempre 0 em propostas novas.
    // Propostas antigas mantêm o valor histórico no Cosmos (não é recalculado).
    dvrs: 0,
    storages: getNum('qtdStorages'),
    outros: getNum('qtdOutros'),
  };
}

function isAvulso(): boolean {
  return (document.getElementById('modoAvulso') as HTMLInputElement | null)?.checked ?? false;
}

function recalcularPricing(): void {
  if (!state.tabela) return;
  const avulso = isAvulso();
  // Modo avulso: esconde seções de pacote base (perfil/plano, estações, inventário).
  document.querySelectorAll<HTMLElement>('.avulso-hide').forEach((el) => {
    el.style.display = avulso ? 'none' : '';
  });
  // Trava: DR e Teste de Restore só liberam com backup de servidor (físico ou virtual 2TB).
  const temServidorBackup =
    getNum('backupServidorFisico2tb') > 0 || getNum('backupServidorVirtual2tb') > 0;
  for (const id of ['draas', 'backupTesteRestore']) {
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (!el) continue;
    el.disabled = !temServidorBackup;
    el.title = temServidorBackup ? '' : 'Requer Backup de Servidor Físico 2TB ou Virtual 2TB';
    if (!temServidorBackup) el.value = '0';
  }
  const pacote = (avulso ? 'essencial' : getStr('pacotePrincipal') || 'professional') as PacoteKey;

  // Horas de suporte só é relevante pro Essencial — esconde nos outros pacotes
  const $horasField = document.getElementById('horas-suporte-field');
  if ($horasField) {
    $horasField.style.display = pacote === 'essencial' ? '' : 'none';
  }

  const onboarding = construirOnboardingInput();

  const breakdown = calcularPricing({
    tabela: state.tabela,
    pacote,
    semPacoteBase: avulso,
    perfil: (getStr('perfil') || 'moderado') as PerfilKey,
    onboarding,
    recursos: {
      dispositivos: getNum('dispositivos'),
      usuarios: getNum('usuarios'),
      servidores: getNum('servidores'),
      // Pacotes não-Essencial ignoram horas mesmo — zerar pra não confundir
      horasSuporte: pacote === 'essencial' ? getNum('horasSuporte') : 0,
    },
    inventario: lerInventario(),
    addons: lerAddons(),
    // HaaS — passa array vazio quando não há itens (mesmo efeito que undefined)
    haasItens: state.haasItens.length > 0 ? state.haasItens : undefined,
    // Módulos Premium — só passa se pacote=premium E houver selecionados
    premiumModulos:
      pacote === 'premium' && state.premiumModulosSelecionados.length > 0
        ? state.premiumModulosSelecionados
        : undefined,
    // Desconto escalonado — só se vendedor ativou E tem ao menos 1 faixa válida
    descontoEscalonado: getDescontoEscalonadoValido(),
    prazoFidelidadeMeses: parsePrazoFidelidadeMeses(getStr('prazoFidelidade')),
  });
  renderPricingPanel(breakdown);
  atualizarVisibilidadePremium();
  recalcularPremiumModulos();
  renderPremiumModulosLista();
  atualizarUnidadesAddons();
  renderCronograma(breakdown);
}

/**
 * Retorna a config de desconto pronta pra pricing-calc, ou undefined se
 * desabilitado ou faixas inválidas. Validação simples: valor > 0 e duração >= 1.
 */
function getDescontoEscalonadoValido(): InputDesconto | undefined {
  if (!state.descontoAtivo) return undefined;
  const faixasOk = state.descontoFaixas.filter(
    (f) => f.valor > 0 && f.duracaoMeses >= 1 && (f.tipo !== 'pct' || f.valor <= 100),
  );
  if (faixasOk.length === 0) return undefined;
  return {
    faixas: faixasOk.map((f) => ({ ...f })),
    aplicarEm: state.descontoAplicarEm,
  };
}

type InputDesconto = {
  faixas: Array<{ tipo: 'pct' | 'valor'; valor: number; duracaoMeses: number; motivo?: string }>;
  aplicarEm: 'mensal' | 'mensal-onboarding';
};

/**
 * Calcula a sugestão dimensional baseada no estado atual do form.
 * Dispositivos de rede extra = switches + roteadoresAp (inventário do cliente).
 */
function calcularSugestaoAtual(): OnboardingSugerido | null {
  if (!state.tabela) return null;
  const inv = lerInventario();
  return calcularOnboardingSugerido(state.tabela, {
    estacoes: getNum('dispositivos'),
    servidores: getNum('servidores'),
    firewalls: inv.firewalls,
    dispositivosRede: inv.switches + inv.roteadoresAp,
    perfil: (getStr('perfil') || 'moderado') as PerfilKey,
  });
}

/**
 * Constrói o OnboardingInput a partir do estado da UI:
 *  - checkbox desligado → { modo: 'nenhum' }
 *  - valor === sugerido → { modo: 'sugerido' }
 *  - valor diferente    → { modo: 'manual' }
 *
 * Também atualiza a UI: breakdown + placeholder + aviso de edição manual.
 */
function construirOnboardingInput(): OnboardingInput {
  const $cobrar = document.getElementById('onboardingCobrar') as HTMLInputElement | null;
  const $valor = document.getElementById('onboardingValor') as HTMLInputElement | null;
  const $bloco = document.getElementById('onboarding-bloco');
  const $aviso = document.getElementById('onboardingEditadoAviso');
  const $bdCont = document.getElementById('onboardingBreakdownConteudo');

  // Toggle "cobrar": desligado oculta inputs + retorna nenhum
  if ($cobrar && !$cobrar.checked) {
    if ($bloco) $bloco.style.opacity = '0.5';
    if ($valor) $valor.disabled = true;
    if ($aviso) $aviso.style.display = 'none';
    return { modo: 'nenhum' };
  }
  if ($bloco) $bloco.style.opacity = '1';
  if ($valor) $valor.disabled = false;

  const sug = calcularSugestaoAtual();
  if (!sug) return { modo: 'nenhum' };

  // Atualiza placeholder e breakdown SEMPRE
  if ($valor) {
    $valor.placeholder = sug.total.toFixed(2);
    // Se o campo está vazio, considera que o usuário aceita a sugestão
    if (!$valor.value || $valor.dataset.userEdited !== 'true') {
      $valor.value = sug.total.toFixed(2);
    }
  }
  if ($bdCont) $bdCont.textContent = formatarBreakdownOnboarding(sug);

  const valorFinal = $valor ? Number($valor.value) || 0 : sug.total;
  const editouManual = Math.abs(valorFinal - sug.total) > 0.005;
  if ($aviso) $aviso.style.display = editouManual ? '' : 'none';

  const breakdown = {
    baseFixo: sug.baseFixo,
    linhas: sug.linhas,
    subtotalAntesMultiplicador: sug.subtotalAntesMultiplicador,
    perfil: sug.perfil,
    multiplicador: sug.multiplicador,
    total: sug.total,
  };

  return {
    modo: editouManual ? 'manual' : 'sugerido',
    valorFinal,
    valorSugerido: sug.total,
    breakdown,
  };
}

/**
 * Monta o texto do breakdown que aparece no <details> do form.
 * Espelha o formato pedido no briefing.
 */
function formatarBreakdownOnboarding(s: OnboardingSugerido): string {
  const partes: string[] = [];
  partes.push(`Base: ${fmtBRL(s.baseFixo)}`);
  for (const l of s.linhas) {
    partes.push(`${l.qtd} × ${l.label} × ${fmtBRL(l.unitario)} = ${fmtBRL(l.subtotal)}`);
  }
  partes.push('─'.repeat(40));
  partes.push(`Subtotal: ${fmtBRL(s.subtotalAntesMultiplicador)}`);
  partes.push(`Multiplicador ${s.perfil} (×${s.multiplicador.toFixed(2)}): ${fmtBRL(s.total)}`);
  partes.push('─'.repeat(40));
  partes.push(`Onboarding sugerido: ${fmtBRL(s.total)}`);
  return partes.join('\n');
}

function atualizarUnidadesAddons(): void {
  if (!state.tabela) return;
  const t = state.tabela;
  const v = t.venda;
  const mapa: Record<string, number> = {
    backupM365: v.backupNuvem.m365,
    backupEstacao500gb: v.backupNuvem.estacao500gb,
    backupServidorFisico2tb: v.backupNuvem.servidorFisico2tb,
    backupServidorVirtual2tb: v.backupNuvem.servidorVirtual2tb,
    backupPerGb: v.backupNuvem.perGb,
    backupTesteRestore: v.backupNuvem.testeRestore,
    draas: v.backupNuvem.draas,
    avGerenciado: v.seguranca.antivirus,
    avEdr: v.seguranca.edr,
    mikrotik5p: v.firewall.mikrotik5p,
    mikrotikWireless: v.firewall.mikrotikWireless,
    gestaoFirewallCliente: v.firewall.gestaoFirewallCliente,
  };
  document.querySelectorAll<HTMLElement>('[data-addon-unit-for]').forEach((el) => {
    const name = el.dataset.addonUnitFor ?? '';
    const preco = mapa[name];
    el.textContent = preco !== undefined ? `${fmtBRL(preco)} / unid.` : '—';
  });
}

/** Label curta pro painel de pricing (não usa o HAAS_TIPO_LABEL pra evitar circular dep). */
function haasTipoLabelPainel(tipo: string): string {
  if (tipo === 'mikrotikWireless') return 'Mikrotik Wireless';
  if (tipo === 'mikrotik') return 'Mikrotik';
  if (tipo === 'accessPoint') return 'Access Point';
  if (tipo === 'switch') return 'Switch';
  if (tipo === 'nobreak') return 'Nobreak';
  return tipo;
}

function renderPricingPanel(b: Breakdown): void {
  const $panel = document.getElementById('pricing-panel');
  if (!$panel) return;

  const renderLinha = (l: LinhaBreakdown): string => `
    <div class="pr-linha">
      <div class="pr-label">${escapeHtml(l.label)}${l.multiplicadorAplicado ? ` <span class="pr-mult">×${b.multiplicador.toFixed(2)}</span>` : ''}</div>
      <div class="pr-qtd">${l.qtd} × ${fmtBRL(l.unitario)}</div>
      <div class="pr-sub">${fmtBRL(l.subtotal)}</div>
    </div>`;

  const renderGrupo = (
    titulo: string,
    linhas: LinhaBreakdown[],
    subtotal: number,
    totalLabel = 'Subtotal',
  ): string => {
    if (!linhas.length) return '';
    return `
      <div class="pr-grupo">
        <div class="pr-grupo-h">${escapeHtml(titulo)}</div>
        ${linhas.map(renderLinha).join('')}
        <div class="pr-linha pr-subtotal">
          <div class="pr-label">${escapeHtml(totalLabel)}</div>
          <div></div>
          <div class="pr-sub">${fmtBRL(subtotal)}</div>
        </div>
      </div>`;
  };

  $panel.innerHTML = `
    <h3 class="pr-titulo">Investimento</h3>

    ${(() => {
      // Pacote + módulos Premium (quando aplicável) somam no mesmo bloco
      const premiumLinhas = (b.premiumModulos?.linhas ?? []).map((l) => ({
        label: l.nome,
        qtd: 1,
        unitario: l.valorMensal,
        subtotal: l.valorMensal,
        multiplicadorAplicado: false,
      }));
      const comboDesc = b.premiumModulos?.todosMarcados ? (b.premiumModulos.comboDesconto ?? 0) : 0;
      const comboLinha =
        comboDesc > 0
          ? [
              {
                label: `Combo Premium (−${b.premiumModulos?.comboPct ?? 0}%)`,
                qtd: 1,
                unitario: -comboDesc,
                subtotal: -comboDesc,
                multiplicadorAplicado: false,
              },
            ]
          : [];
      const linhasCompletas = [...b.pacote.linhas, ...premiumLinhas, ...comboLinha];
      const subtotal = b.pacote.subtotal + (b.premiumModulos?.totalLiquido ?? 0);
      return renderGrupo(`Pacote ${b.pacote.nome}`, linhasCompletas, subtotal, 'Subtotal pacote');
    })()}
    ${renderGrupo('Dispositivos do parque', b.inventario.linhas, b.inventario.subtotal, 'Subtotal dispositivos')}
    ${renderGrupo('Add-ons', b.addons.linhas, b.addons.subtotal, 'Subtotal add-ons')}
    ${
      b.haas && b.haas.linhas.length > 0
        ? `
      <div class="pr-grupo">
        <div class="pr-grupo-h">HaaS — Hardware como Serviço</div>
        ${b.haas.linhas
          .map(
            (l) => `
          <div class="pr-linha">
            <div class="pr-linha-label">${escapeHtml(haasTipoLabelPainel(l.tipo))} ${l.modelo ? `<span style="color:var(--color-text-muted);font-size:11px">— ${escapeHtml(l.modelo)}</span>` : ''}</div>
            <div class="pr-linha-mid">${l.qtd} × ${fmtBRL(l.valorMensalUnit)}</div>
            <div class="pr-linha-valor">${fmtBRL(l.subtotalMensal)}</div>
          </div>
        `,
          )
          .join('')}
        <div class="pr-subtotal">
          <div class="pr-subtotal-label">Subtotal HaaS</div>
          <div class="pr-subtotal-valor">${fmtBRL(b.haas.totalMensal)}</div>
        </div>
      </div>`
        : ''
    }


    ${
      !b.pacote.linhas.length &&
      !b.inventario.linhas.length &&
      !b.addons.linhas.length &&
      !(b.haas && b.haas.linhas.length) &&
      !(b.premiumModulos && b.premiumModulos.linhas.length)
        ? '<div class="pr-vazio">Preencha estações/usuários para ver o cálculo.</div>'
        : ''
    }

    <div class="pr-total">
      <div class="pr-total-label">Mensal recorrente</div>
      <div class="pr-total-valor">${fmtBRL(b.mensalRecorrente)}</div>
    </div>

    ${
      b.onboarding.total > 0
        ? `
      <div class="pr-onboarding">
        <div class="pr-grupo-h">Onboarding ${escapeHtml(b.onboarding.tipo)}</div>
        <div class="pr-linha">
          <div class="pr-label">Total</div>
          <div></div>
          <div class="pr-sub">${fmtBRL(b.onboarding.total)}</div>
        </div>
        <div class="pr-linha">
          <div class="pr-label">Parcela (6×)</div>
          <div></div>
          <div class="pr-sub">${fmtBRL(b.onboarding.parcela)}</div>
        </div>
        <div class="pr-linha pr-subtotal">
          <div class="pr-label">Mensal nos 6 primeiros meses</div>
          <div></div>
          <div class="pr-sub">${fmtBRL(b.mensalPrimeiros6Meses)}</div>
        </div>
      </div>
    `
        : ''
    }

    <div class="pr-rodape">
      <div>Perfil <strong>${b.perfil}</strong> · multiplicador <strong>×${b.multiplicador.toFixed(2)}</strong></div>
      <div>Total estimado em 12 meses: <strong>${fmtBRL(b.totalContrato12m)}</strong></div>
    </div>
  `;
}

// ─── Omie lookup ─────────────────────────────────────────────────────────

async function lookupOmie(docRaw: string): Promise<void> {
  const doc = docRaw.replace(/\D/g, '');
  const $help = document.getElementById('documento-help');
  if (!$help) return;

  if (doc.length !== 11 && doc.length !== 14) {
    $help.textContent = 'Documento inválido — informe CPF (11 dígitos) ou CNPJ (14 dígitos).';
    $help.style.color = 'var(--color-error, #c00)';
    return;
  }

  state.loadingOmie = true;
  $help.textContent = 'Buscando no Omie…';
  $help.style.color = 'var(--color-text-muted)';

  try {
    const { cliente } = await api.clientes.lookupOmie(doc);
    setValue('empresa', cliente.empresa);
    setValue('responsavel', cliente.responsavel);
    setValue('email', cliente.email);
    if (cliente.telefone) setValue('telefone', cliente.telefone);
    $help.textContent = `OK — ${cliente.empresa} carregado do Omie.`;
    $help.style.color = 'var(--color-success, #0a0)';
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      $help.textContent = 'Cliente não encontrado no Omie. Preencha manualmente.';
    } else {
      $help.textContent = `Falha ao consultar Omie: ${err instanceof Error ? err.message : 'erro desconhecido'}`;
    }
    $help.style.color = 'var(--color-error, #c00)';
  } finally {
    state.loadingOmie = false;
  }
}

function setValue(name: string, value: string): void {
  const el = document.getElementById(name) as HTMLInputElement | null;
  if (el) el.value = value;
}

// ─── Submit ──────────────────────────────────────────────────────────────

async function submitForm(
  form: HTMLFormElement,
  vendedorEmail: string,
  vendedorNome: string,
): Promise<void> {
  if (state.submitting) return;
  state.submitting = true;
  state.errors = {};
  state.topLevelError = null;
  clearErrors();
  setSubmitting(true);

  const data = new FormData(form);
  const get = (name: string): string => String(data.get(name) ?? '').trim();
  const getNumF = (name: string): number => Number(data.get(name) ?? 0);

  const avulso = isAvulso();
  const docDigits = get('documento').replace(/\D/g, '');
  const isCnpj = docDigits.length === 14;
  const isCpf = docDigits.length === 11;

  const observacoesMd = montarObservacoesEstruturadas({
    problemasAtuais: get('problemasAtuais'),
    seguranca: {
      antivirus: get('segAntivirus'),
      edr: get('segEdr'),
      backup: get('segBackup'),
      backupTestado: get('segBackupTestado'),
    },
    inventario: {
      impressoras: getNumF('qtdImpressoras'),
      nobreaks: getNumF('qtdNobreaks'),
      firewalls: getNumF('qtdFirewalls'),
      roteadoresAp: getNumF('qtdRoteadoresAp'),
      switches: getNumF('qtdSwitches'),
      dvrs: getNumF('qtdDvrs'),
      storages: getNumF('qtdStorages'),
      outros: getNumF('qtdOutros'),
    },
    addons: lerAddons(),
    observacoesLivres: get('observacoesLivres'),
  });

  const input: CriarPropostaInput = {
    tipo: 'contrato-recorrente',
    cliente: {
      empresa: get('empresa'),
      cnpj: isCnpj ? docDigits : undefined,
      cpf: isCpf ? docDigits : undefined,
      responsavel: get('responsavel'),
      email: get('email'),
      telefone: get('telefone').replace(/\D/g, '') || undefined,
    },
    vendedor: { email: vendedorEmail, nome: vendedorNome },
    validade: get('validade'),
    payload: {
      pacotePrincipal: avulso
        ? 'essencial'
        : (get('pacotePrincipal') as 'essencial' | 'professional' | 'premium'),
      ...(avulso ? { semPacoteBase: true } : {}),
      pacotesAdicionais: [],
      ambiente: {
        perfil: avulso ? 'moderado' : (get('perfil') as 'simples' | 'moderado' | 'critico'),
        multiplicador: 1,
      },
      recursos: avulso
        ? { dispositivos: 0, usuarios: 0, servidores: 0, horasSuporte: 0 }
        : {
            dispositivos: getNumF('dispositivos'),
            usuarios: getNumF('usuarios'),
            servidores: getNumF('servidores'),
            horasSuporte: getNumF('horasSuporte'),
          },
      onboarding: construirOnboardingInput(),
      prazoFidelidade: get('prazoFidelidade') as
        | '12 meses'
        | '24 meses'
        | '36 meses'
        | 'Sem fidelidade',
      // HaaS — só inclui se houver itens (omitido = sem Adendo HaaS)
      ...(state.haasItens.length > 0 ? { haasItens: state.haasItens } : {}),
      // Módulos Premium — só inclui se pacote=premium E houver módulos marcados
      ...(!avulso && state.premiumModulosSelecionados.length > 0
        ? { premiumModulos: state.premiumModulosSelecionados }
        : {}),
      // Desconto escalonado — só inclui se vendedor ativou E tem ao menos 1 faixa válida
      ...(getDescontoEscalonadoValido()
        ? { descontoEscalonado: getDescontoEscalonadoValido() }
        : {}),
      // #255 Congelar reajuste — marcador comercial, só inclui se ativo e duração válida
      ...(state.congelarReajusteAtivo &&
      state.congelarReajusteMeses >= 1 &&
      state.congelarReajusteMeses <= 36
        ? { congelarReajusteMeses: state.congelarReajusteMeses }
        : {}),
    },
    observacoes: observacoesMd || undefined,
    observacoesInternas: get('observacoesInternas') || undefined,
    exibicao: {
      mostrarValoresUnitarios:
        (document.getElementById('mostrarValoresUnitarios') as HTMLInputElement | null)?.checked ??
        true,
      modoAddOns: (document.getElementById('modoAddOnsCompleto') as HTMLInputElement | null)
        ?.checked
        ? 'completo'
        : 'compacto',
    },
  };

  const parsed = criarPropostaContratoRecorrenteSchema.safeParse(input);
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      const fieldName = String(issue.path.at(-1) ?? '');
      state.errors[fieldName] = issue.message;
    }
    state.topLevelError = 'Corrija os campos destacados.';
    renderErrors();
    setSubmitting(false);
    state.submitting = false;
    return;
  }

  try {
    let proposta: { id: string };
    if (state.modoEdicao && state.propostaId) {
      // PATCH parcial — backend faz merge raso em cliente/payload/exibicao
      const patch = {
        cliente: parsed.data.cliente,
        validade: parsed.data.validade,
        observacoes: parsed.data.observacoes,
        observacoesInternas: parsed.data.observacoesInternas,
        payload: parsed.data.payload,
        exibicao: parsed.data.exibicao,
      };
      const r = await api.propostas.update(state.propostaId, patch);
      proposta = r.proposta;
    } else {
      const r = await api.propostas.create(parsed.data);
      proposta = r.proposta;
    }
    getRouter().navigate(`/propostas/${encodeURIComponent(proposta.id)}`);
  } catch (err) {
    if (err instanceof ApiError) {
      state.topLevelError = `${err.code ?? 'ERRO'}: ${err.message}`;
    } else {
      state.topLevelError =
        err instanceof Error ? err.message : 'Falha desconhecida ao criar proposta.';
    }
    renderErrors();
  } finally {
    setSubmitting(false);
    state.submitting = false;
  }
}

function clearErrors(): void {
  document.querySelectorAll<HTMLElement>('.form-field-error').forEach((el) => el.remove());
  document
    .querySelectorAll<HTMLElement>('.form-field-invalid')
    .forEach((el) => el.classList.remove('form-field-invalid'));
  const $top = document.getElementById('top-error');
  if ($top) {
    $top.style.display = 'none';
    $top.textContent = '';
  }
}

function renderErrors(): void {
  clearErrors();
  const $top = document.getElementById('top-error');
  if (state.topLevelError && $top) {
    $top.textContent = state.topLevelError;
    $top.style.display = 'block';
  }
  for (const [field, msg] of Object.entries(state.errors)) {
    const $input = document.getElementById(field);
    const $label = $input?.closest<HTMLElement>('label.form-field');
    if (!$label) continue;
    $label.classList.add('form-field-invalid');
    const $err = document.createElement('small');
    $err.className = 'form-field-error';
    $err.style.color = 'var(--color-error, #c00)';
    $err.textContent = escapeHtml(msg);
    $label.appendChild($err);
  }
}

// ─── Helpers utilitários ─────────────────────────────────────────────────

function setSubmitting(on: boolean): void {
  const $btn = document.getElementById('btn-submit') as HTMLButtonElement | null;
  if ($btn) {
    $btn.disabled = on;
    $btn.textContent = on ? 'Salvando…' : state.modoEdicao ? 'Salvar alterações' : 'Criar proposta';
  }
}

interface ObsInput {
  problemasAtuais: string;
  seguranca: { antivirus: string; edr: string; backup: string; backupTestado: string };
  inventario: {
    impressoras: number;
    nobreaks: number;
    firewalls: number;
    roteadoresAp: number;
    switches: number;
    dvrs: number;
    storages: number;
    outros: number;
  };
  addons: AddOns;
  observacoesLivres: string;
}

function montarObservacoesEstruturadas(o: ObsInput): string {
  const partes: string[] = [];
  if (o.problemasAtuais && o.problemasAtuais.trim()) {
    partes.push(`## Problemas atuais\n${o.problemasAtuais.trim()}`);
  }
  const seg: string[] = [];
  if (o.seguranca.antivirus) seg.push(`- Antivírus: ${o.seguranca.antivirus}`);
  if (o.seguranca.edr) seg.push(`- EDR: ${o.seguranca.edr}`);
  if (o.seguranca.backup) seg.push(`- Backup: ${o.seguranca.backup}`);
  if (o.seguranca.backupTestado) seg.push(`- Backup testado: ${o.seguranca.backupTestado}`);
  if (seg.length) partes.push(`## Segurança & Backup\n${seg.join('\n')}`);
  const inv: string[] = [];
  if (o.inventario.impressoras > 0) inv.push(`- Impressoras: ${o.inventario.impressoras}`);
  if (o.inventario.nobreaks > 0) inv.push(`- Nobreaks: ${o.inventario.nobreaks}`);
  if (o.inventario.firewalls > 0) inv.push(`- Firewall: ${o.inventario.firewalls}`);
  if (o.inventario.roteadoresAp > 0) inv.push(`- Roteador/AP: ${o.inventario.roteadoresAp}`);
  if (o.inventario.switches > 0) inv.push(`- Switch: ${o.inventario.switches}`);
  if (o.inventario.dvrs > 0) inv.push(`- DVR: ${o.inventario.dvrs}`);
  if (o.inventario.storages > 0) inv.push(`- Storage: ${o.inventario.storages}`);
  if (o.inventario.outros > 0) inv.push(`- Outros: ${o.inventario.outros}`);
  if (inv.length) partes.push(`## Inventário do parque\n${inv.join('\n')}`);
  const adds: string[] = [];
  const addonsLabels: Record<keyof AddOns, string> = {
    backupM365: 'Backup M365',
    backupEstacao500gb: 'Backup Estação 500GB',
    backupServidorFisico2tb: 'Backup Servidor Físico 2TB',
    backupServidorVirtual2tb: 'Backup Servidor Virtual 2TB',
    backupPerGb: 'Backup por GB',
    backupTesteRestore: 'Teste de Restore',
    draas: 'DRaaS',
    avGerenciado: 'AV Gerenciado',
    avEdr: 'AV EDR',
    mikrotik5p: 'Mikrotik 5p',
    mikrotikWireless: 'Mikrotik Wireless',
    gestaoFirewallCliente: 'Gestão Firewall Cliente',
  };
  for (const k of Object.keys(addonsLabels) as (keyof AddOns)[]) {
    const v = o.addons[k] ?? 0;
    if (v > 0) adds.push(`- ${addonsLabels[k]}: ${v}`);
  }
  if (adds.length) partes.push(`## Add-ons mensais\n${adds.join('\n')}`);
  if (o.observacoesLivres && o.observacoesLivres.trim()) {
    partes.push(`## Observações\n${o.observacoesLivres.trim()}`);
  }
  return partes.join('\n\n');
}

// Estilos agora vêm de proposta-nova.css (import no topo do arquivo).
// Stub vazio só pra não quebrar a chamada existente em renderNovaProposta.
function injectFormStyles(): void {
  // no-op
}
